import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { useDepartment } from '../../context/DepartmentContext'
import { useToast } from '../../context/ToastContext'
import { getDistributionPoints, addDistributionPoint, removeDistributionPoint, SHIFTS } from '../../data/bakeryData'
import { getYawehsDistributionPoints, addYawehsDistributionPoint, removeYawehsDistributionPoint, YAWEHS_SHIFTS } from '../../data/yawehsData'
import { getProducts } from '../../data/products'
import { authedFetch } from '../../lib/authedFetch'
import {
  saveProductionBatch, saveDistributionBatch,
  getProductionLogs, getDistributionLogs,
  saveExpenditureBatch, getExpenditure,
  saveCashOnHand, getCashOnHand,
  deleteDistributionForPoint,
  deleteProductionForDate, deleteExpenditureForDate, deleteCashOnHandForDate,
  saveSaleTransaction, saveSalesBatch, getSalesTransactions, deleteSalesForDate,
  saveLocationCash, getLocationCash, deleteLocationCash,
  getDailyCollections, saveDailyCollectionsBatch, getBakeryProjectedForSite, getMissingProductCodesForSite,
  getDamages, saveDamagesBatch,
  getCreditEntries, saveCreditEntry, deleteCreditEntry,
} from '../../lib/store'
import { Save, Plus, Trash2, MapPin, AlertTriangle, X, Banknote, Wallet, Download, Upload, ArrowLeft, RefreshCw, Link2, Eye, Edit3, Search, ShoppingBag, Store } from 'lucide-react'
import DateInput from '../ui/DateInput'
import ArithmeticInput from '../ui/ArithmeticInput'
import ArithmeticTip from '../ui/ArithmeticTip'
import AddProductModal from './AddProductModal'
import { getProductPrice } from '../../lib/productPricing'
import { getSitePriceMap, refreshBakeryDistributionPrices } from '../../lib/bakeryPricingStore'

// Branch-aware product price resolver. Legacy docs (no prices map) fall
// back to unitPrice. Null (not sold at branch) coerces to 0.
function priceFor(product, dept) {
  const px = getProductPrice(product, dept)
  return px == null ? 0 : Number(px) || 0
}

// Helper to get department-specific config
function useDeptConfig() {
  const { currentDepartmentId } = useDepartment()
  const dept = currentDepartmentId
  const isYawehs = dept === 'yawehs'
  return {
    dept,
    isYawehs,
    products: getProducts(dept),
    shifts: isYawehs ? YAWEHS_SHIFTS : SHIFTS,
    getDistPoints: isYawehs ? getYawehsDistributionPoints : getDistributionPoints,
    addDistPoint: isYawehs ? addYawehsDistributionPoint : addDistributionPoint,
    removeDistPoint: isYawehs ? removeYawehsDistributionPoint : removeDistributionPoint,
  }
}

// --- Production Log (Night/Day shift) ---
function ProductionForm({ date, onSaved }) {
  const { addToast } = useToast()
  const { dept, shifts } = useDeptConfig()
  const [shift, setShift] = useState('night')
  const [values, setValues] = useState({})
  const [viewMode, setViewMode] = useState(false)
  const [showAddProduct, setShowAddProduct] = useState(false)
  const [productRefresh, setProductRefresh] = useState(0)
  const [searchTerm, setSearchTerm] = useState('')
  const products = useMemo(() => getProducts(dept), [dept, productRefresh])
  const filteredProducts = searchTerm
    ? products.filter(p => p.name.toLowerCase().includes(searchTerm.toLowerCase()) || p.code.toLowerCase().includes(searchTerm.toLowerCase()))
    : products

  const updateValue = (code, qty) => {
    setValues(prev => ({ ...prev, [code]: qty }))
  }

  const loadExisting = useCallback(() => {
    const logs = getProductionLogs(date, shift, dept)
    if (logs.length > 0) {
      const v = {}
      for (const l of logs) v[l.productCode] = l.qtyProduced
      setValues(v)
      setViewMode(true)
    } else {
      setValues({})
      setViewMode(false)
    }
  }, [date, shift, dept])

  // Auto-load when date/shift changes
  useEffect(() => { loadExisting() }, [loadExisting])

  const handleSave = () => {
    const entries = Object.entries(values)
      .filter(([, qty]) => qty !== '' && qty !== undefined && qty !== null && Number(qty) > 0)
      .map(([productCode, qtyProduced]) => ({ productCode, qtyProduced }))

    if (entries.length === 0) {
      addToast('No quantities entered', 'warning')
      return
    }

    saveProductionBatch(date, shift, entries, dept)
    addToast(`Saved ${entries.length} production entries (${shift} shift)`, 'success')
    setViewMode(true)
    onSaved?.()
  }

  const handleDelete = () => {
    if (!confirm(`Delete all production entries for ${date}?`)) return
    const count = deleteProductionForDate(date, dept)
    addToast(`Deleted ${count} production entries`, 'success')
    setValues({})
    setViewMode(false)
    onSaved?.()
  }

  const hasData = Object.values(values).some(v => v !== '' && v !== undefined && v !== null && Number(v) > 0)

  // ─── View Mode ───
  if (viewMode && hasData) {
    const filledProducts = products.filter(p => values[p.code] && Number(values[p.code]) > 0)
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-4 flex-wrap">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Shift</label>
            <div className="flex gap-1">
              {shifts.map(s => (
                <button
                  key={s.value}
                  onClick={() => { setShift(s.value); setTimeout(() => loadExisting(), 0) }}
                  className={`px-4 py-2 rounded text-sm font-medium ${
                    shift === s.value ? 'bg-brand-700 text-white' : 'bg-gray-800 text-gray-400 hover:text-white'
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-green-400 text-sm">
            <Eye size={16} /> Saved Production — {filledProducts.length} items ({shift} shift)
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setViewMode(false)}
              className="flex items-center gap-1 bg-gray-800 hover:bg-gray-700 text-white px-3 py-1.5 rounded text-xs"
            >
              <Edit3 size={12} /> Edit
            </button>
            <button
              onClick={handleDelete}
              className="flex items-center gap-1 bg-gray-800 hover:bg-red-900/50 text-red-400 px-3 py-1.5 rounded text-xs"
            >
              <Trash2 size={12} /> Delete
            </button>
          </div>
        </div>

        <div className="space-y-1">
          {filledProducts.map(p => (
            <div key={p.code} className="flex items-center justify-between bg-gray-800/50 rounded px-4 py-2">
              <div className="flex items-center gap-3">
                <span className="font-mono text-xs text-gray-500">{p.code}</span>
                <span className="text-sm">{p.name}</span>
              </div>
              <span className="text-sm font-medium text-white">{values[p.code]} units</span>
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4 flex-wrap">
        <div>
          <label className="block text-xs text-gray-400 mb-1">Shift</label>
          <div className="flex gap-1">
            {shifts.map(s => (
              <button
                key={s.value}
                onClick={() => { setShift(s.value); setTimeout(() => loadExisting(), 0) }}
                className={`px-4 py-2 rounded text-sm font-medium ${
                  shift === s.value ? 'bg-brand-700 text-white' : 'bg-gray-800 text-gray-400 hover:text-white'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="relative mb-2">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
        <input
          type="text"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          placeholder="Search products..."
          className="w-full bg-gray-900 border border-gray-700 rounded pl-9 pr-3 py-1.5 text-sm text-white placeholder:text-gray-500"
        />
      </div>

      <div className="space-y-1 max-h-[60vh] overflow-y-auto">
        {filteredProducts.map(p => (
          <div key={p.code} className="flex items-center gap-4 bg-gray-800/50 rounded px-4 py-1.5">
            <span className="w-10 font-mono text-xs text-gray-500">{p.code}</span>
            <span className="flex-1 text-sm">{p.name}</span>
            <span className="text-gray-600 text-[10px] w-20 capitalize">{p.category}</span>
            <span className="text-gray-500 text-xs w-14">R{priceFor(p, dept) || '—'}</span>
            <ArithmeticInput
              min="0"
              value={values[p.code] ?? ''}
              onChange={(val) => updateValue(p.code, val == null ? '' : val)}
              className="bg-gray-900 border border-gray-700 rounded px-3 py-1 text-sm text-white w-24"
              placeholder="0"
            />
          </div>
        ))}
      </div>

      {/* Day total */}
      {(() => {
        const totalItems = Object.values(values).reduce((s, v) => s + (Number(v) || 0), 0)
        const totalProducts = Object.values(values).filter(v => Number(v) > 0).length
        return totalItems > 0 ? (
          <div className="text-xs text-gray-400 px-2">
            Total produced: <span className="text-white font-medium">{totalItems} items</span> across <span className="text-white font-medium">{totalProducts} products</span>
          </div>
        ) : null
      })()}

      <div className="flex items-center gap-3">
        <button
          onClick={handleSave}
          className="flex items-center gap-2 bg-brand-600 hover:bg-brand-500 text-white px-6 py-2.5 rounded-lg text-sm font-medium"
        >
          <Save size={16} /> Save Production ({shift} shift)
        </button>
        <button
          onClick={() => setShowAddProduct(true)}
          className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 text-gray-300 px-4 py-2.5 rounded-lg text-sm font-medium"
        >
          <Plus size={16} /> Add Product
        </button>
      </div>

      {showAddProduct && (
        <AddProductModal
          department={dept}
          onClose={() => setShowAddProduct(false)}
          onProductAdded={() => setProductRefresh(k => k + 1)}
        />
      )}
    </div>
  )
}

// --- Distribution Form (per distribution point, with file upload for Reported Sales) ---
function DistributionForm({ date: dateProp, onSaved }) {
  const { addToast } = useToast()
  const { dept, products, getDistPoints, addDistPoint, removeDistPoint } = useDeptConfig()
  const [date, setDate] = useState(dateProp)
  useEffect(() => setDate(dateProp), [dateProp])
  const [pointId, setPointId] = useState('')
  const [values, setValues] = useState({})
  const [showAddPoint, setShowAddPoint] = useState(false)
  const [newPointName, setNewPointName] = useState('')
  const [refreshKey, setRefreshKey] = useState(0)
  const [uploadUnmatched, setUploadUnmatched] = useState([])
  const [yocoPulling, setYocoPulling] = useState(false)
  const [yocoConnected, setYocoConnected] = useState(null) // null = unknown, true/false
  const [viewMode, setViewMode] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const fileInputRef = useRef(null)

  const points = useMemo(() => getDistPoints(), [refreshKey])

  // Site-price map: { productCode → effectivePrice } for the selected
  // site. Recomputes on site change and when the overrides cache is
  // hydrated from Firestore (priceRefreshKey bump).
  const [priceRefreshKey, setPriceRefreshKey] = useState(0)
  useEffect(() => {
    refreshBakeryDistributionPrices(dept)
      .then(() => setPriceRefreshKey(k => k + 1))
      .catch(() => { /* silent — getSitePriceMap falls back to base */ })
  }, [dept])
  const sitePrices = useMemo(
    () => (pointId ? getSitePriceMap(dept, pointId, products) : {}),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dept, pointId, products, priceRefreshKey]
  )
  const priceForRow = (product) => {
    if (pointId && product?.code && sitePrices[product.code] != null) {
      return Number(sitePrices[product.code]) || 0
    }
    return priceFor(product, dept)
  }

  const updateField = (code, field, val) => {
    setValues(prev => ({
      ...prev,
      [code]: { ...prev[code], [field]: val }
    }))
  }

  const loadExisting = useCallback(() => {
    if (!pointId) return
    const logs = getDistributionLogs(date, pointId, dept)
    const v = {}
    for (const l of logs) {
      v[l.productCode] = {
        sent: l.qtySent,
        reportedSales: l.qtySold,
        reportedSurplus: l.reportedSurplus ?? '',
        damages: l.damages ?? '',
        // Preserve the frozen unitPrice so view-mode renders the price
        // that was ACTUALLY used at save time, not today's base price.
        frozenUnitPrice: l.unitPrice,
      }
    }
    setValues(v)
    setUploadUnmatched([])
    if (logs.length > 0) addToast(`Loaded ${logs.length} distribution entries`, 'info')
  }, [date, pointId, dept])

  const handleAddPoint = () => {
    if (!newPointName.trim()) return
    addDistPoint({ name: newPointName.trim(), shortName: newPointName.trim().split(' ')[0] })
    setRefreshKey(k => k + 1)
    setNewPointName('')
    setShowAddPoint(false)
    addToast(`Added distribution point "${newPointName}"`, 'success')
  }

  const handleRemovePoint = (id) => {
    if (!confirm('Remove this distribution point?')) return
    removeDistPoint(id)
    setRefreshKey(k => k + 1)
    if (pointId === id) setPointId('')
    addToast('Distribution point removed', 'success')
  }

  const selectPoint = (id) => {
    setPointId(id)
    setValues({})
    setUploadUnmatched([])
    // Auto-load after selecting
    setTimeout(() => {
      const logs = getDistributionLogs(date, id, dept)
      const v = {}
      for (const l of logs) {
        v[l.productCode] = {
          sent: l.qtySent,
          reportedSales: l.qtySold,
          reportedSurplus: l.reportedSurplus ?? '',
          damages: l.damages ?? '',
          frozenUnitPrice: l.unitPrice,
        }
      }
      setValues(v)
      setViewMode(logs.length > 0)
    }, 0)
  }

  const handleDelete = () => {
    if (!confirm(`Delete all distribution entries for ${pointName} on ${date}?`)) return
    const count = deleteDistributionForPoint(date, pointId, dept)
    addToast(`Deleted ${count} entries`, 'success')
    setValues({})
    setViewMode(false)
    onSaved?.()
  }

  const handleSave = () => {
    if (!pointId) { addToast('Select a distribution point', 'warning'); return }
    const entries = Object.entries(values)
      .filter(([, v]) => v && (v.sent || v.reportedSurplus || v.damages))
      .map(([productCode, v]) => {
        // Resolve SITE-SPECIFIC price (override → branch → legacy) so
        // saveDistributionBatch can freeze it onto the record. Future
        // base-price changes don't retro-affect this distribution.
        const prod = products.find(p => p.code === productCode)
        return {
          productCode,
          qtySent: Number(v.sent) || 0,
          reportedSurplus: Number(v.reportedSurplus) || 0,
          damages: Number(v.damages) || 0,
          unitPrice: priceForRow(prod),
        }
      })

    if (entries.length === 0) {
      addToast('No entries to save', 'warning')
      return
    }

    saveDistributionBatch(date, pointId, entries, dept)
    const pName = points.find(p => p.id === pointId)?.name || pointId
    addToast(`Saved ${entries.length} items to ${pName}`, 'success')
    loadExisting()
    setViewMode(true)
    onSaved?.()
  }

  // --- File upload for Reported Sales ---

  // Normalize a product/item name for fuzzy matching
  const normalizeName = (s) => s.toLowerCase()
    .replace(/\bn\b/g, '&')           // "n" → "&"
    .replace(/schoolbuns/g, 'school buns')
    .replace(/(\d)scb\b/g, '$1 school buns') // "4SCB" → "4 school buns"
    .replace(/\bscb\b/g, 'school bun')       // "SCB" → "school bun"
    .replace(/[^a-z0-9&\s]/g, '')
    .replace(/\s+/g, ' ')
    .trim()

  // Match an item name against the product list (works for Yoco + Staff POS formats)
  const matchProduct = (itemName) => {
    const norm = normalizeName(itemName)
    // 1. Exact match on normalized name or code
    let match = products.find(p => normalizeName(p.name) === norm || p.code.toLowerCase() === norm)
    if (match) return match
    // 2. Try first word as product code (Staff POS format: "FMK FULL MUKOKOROSHI")
    const firstWord = itemName.trim().split(/\s+/)[0]
    if (firstWord && !/^\d{5,}$/.test(firstWord)) { // skip barcodes
      match = products.find(p => p.code.toLowerCase() === firstWord.toLowerCase())
      if (match) return match
    }
    // 3. Partial: product name contains item or item contains product name (min 4 chars)
    if (norm.length >= 4) {
      match = products.find(p => {
        const pn = normalizeName(p.name)
        return pn.includes(norm) || norm.includes(pn)
      })
      if (match) return match
    }
    return null
  }

  // Items to silently skip from Yoco transactions (payment methods, non-food items)
  const SKIP_YOCO_ITEMS = /^(card payment|card charge|water|juice|cooldrink|coke|fanta|sprite|drink|creamer)/i

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    // Reset file input so the same file can be re-uploaded
    if (fileInputRef.current) fileInputRef.current.value = ''

    const ext = file.name.split('.').pop().toLowerCase()
    let rows = []

    if (ext === 'xlsx' || ext === 'xls') {
      const XLSX = await import('xlsx')
      const data = await file.arrayBuffer()
      const wb = XLSX.read(data, { cellDates: true })
      const ws = wb.Sheets[wb.SheetNames[0]]
      const json = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '', raw: true })

      // --- Detect Yoco transaction format ---
      const headerRow = json[0] || []
      const headerLower = headerRow.map(h => String(h).toLowerCase().trim())
      const itemsColIdx = headerLower.indexOf('items')
      const isYocoFormat = headerLower.includes('receipt') && itemsColIdx >= 0

      if (isYocoFormat) {
        // Parse Yoco POS transaction file: aggregate "qty x ItemName" from Items column
        // Supports multiday files — groups by date, saves each day's sales
        const dateColIdx = headerLower.indexOf('date')
        const statusColIdx = headerLower.indexOf('status')

        // Group items by date (normalize "2026/03/14" → "2026-03-14")
        const byDate = {} // date → { itemName → totalQty }
        for (let i = 1; i < json.length; i++) {
          const row = json[i]
          // Skip non-approved transactions (Error, Declined, etc.)
          if (statusColIdx >= 0) {
            const status = String(row[statusColIdx] || '').trim().toLowerCase()
            if (status && status !== 'approved') continue
          }
          const itemsStr = String(row[itemsColIdx] || '').trim()
          if (!itemsStr) continue
          // Get date for this row (fall back to current selected date)
          let rowDate = date
          if (dateColIdx >= 0 && row[dateColIdx]) {
            const raw = row[dateColIdx]
            if (raw instanceof Date) {
              // cellDates: true parses Excel dates as JS Date objects
              const y = raw.getFullYear()
              const m = String(raw.getMonth() + 1).padStart(2, '0')
              const d = String(raw.getDate()).padStart(2, '0')
              rowDate = `${y}-${m}-${d}`
            } else {
              rowDate = String(raw).trim().replace(/\//g, '-')
              // Handle "DD-MM-YYYY" or "YYYY-MM-DD" — normalize to YYYY-MM-DD
              if (/^\d{2}-\d{2}-\d{4}$/.test(rowDate)) {
                const [dd, mm, yyyy] = rowDate.split('-')
                rowDate = `${yyyy}-${mm}-${dd}`
              }
            }
          }
          if (!byDate[rowDate]) byDate[rowDate] = {}
          for (const part of itemsStr.split(',')) {
            const m = part.trim().match(/^(\d+)\s*x\s+(.+)$/i)
            if (!m) continue
            const qty = Number(m[1])
            const name = m[2].trim()
            if (SKIP_YOCO_ITEMS.test(name)) continue
            byDate[rowDate][name] = (byDate[rowDate][name] || 0) + qty
          }
        }

        const allDates = Object.keys(byDate).sort()
        const isMultiDay = allDates.length > 1

        if (isMultiDay && pointId) {
          // Multiday: save each date's sales directly, fill current date in form
          let totalSaved = 0
          let totalUnmatched = 0
          const unmatchedNames = []

          for (const d of allDates) {
            const dayItems = byDate[d]
            const entries = []
            for (const [name, qty] of Object.entries(dayItems)) {
              const product = matchProduct(name)
              if (product) {
                entries.push({
                  productCode: product.code,
                  qtySent: 0,
                  qtySold: qty,
                  salesRands: qty * priceFor(product, dept),
                  reportedSurplus: 0,
                  damages: 0,
                  unitPrice: priceFor(product, dept),
                })
              } else {
                if (!unmatchedNames.includes(name)) unmatchedNames.push(name)
                totalUnmatched++
              }
            }
            if (entries.length > 0) {
              saveDistributionBatch(d, pointId, entries, dept)
              totalSaved += entries.length
            }
            // Also fill values for the currently selected date
            if (d === date) {
              for (const [name, qty] of Object.entries(dayItems)) {
                const product = matchProduct(name)
                if (product) {
                  setValues(prev => ({
                    ...prev,
                    [product.code]: {
                      ...prev[product.code],
                      reportedSales: (Number(prev[product.code]?.reportedSales) || 0) + qty
                    }
                  }))
                }
              }
            }
          }

          setUploadUnmatched(unmatchedNames)
          addToast(
            `Saved ${totalSaved} items across ${allDates.length} days (${allDates[0]} to ${allDates[allDates.length - 1]})` +
            (unmatchedNames.length > 0 ? ` — ${unmatchedNames.length} unmatched` : ''),
            unmatchedNames.length > 0 ? 'warning' : 'success'
          )
          loadExisting()
          onSaved?.()
          return
        }

        // Single day (or no point selected): fill current date values as before
        const currentDayItems = isMultiDay ? (byDate[date] || byDate[allDates[0]] || {}) : (byDate[allDates[0]] || {})
        rows = Object.entries(currentDayItems).map(([name, qty]) => ({ name, qty }))
      } else {
        // Generic format: [productName, qty] or [productName, ..., qty]
        const headerIdx = json.findIndex(r =>
          r.some(c => typeof c === 'string' && /product|item|name/i.test(String(c).trim()))
        )
        const dataStart = headerIdx >= 0 ? headerIdx + 1 : 0
        for (let i = dataStart; i < json.length; i++) {
          const row = json[i]
          if (!row || !row[0]) continue
          const name = String(row[0]).trim()
          let qty = null
          for (let j = 1; j < row.length; j++) {
            const v = Number(row[j])
            if (!isNaN(v) && v > 0) { qty = v; break }
          }
          if (name && qty !== null) rows.push({ name, qty })
        }
      }
    } else if (ext === 'pdf') {
      const { configurePdfWorker, pdfjsLib } = await import('../../lib/pdfWorker')
      configurePdfWorker()
      const data = await file.arrayBuffer()
      const pdf = await pdfjsLib.getDocument({ data }).promise

      // Extract all lines across all pages
      const allLines = []
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i)
        const content = await page.getTextContent()
        const items = content.items.filter(it => it.str.trim())
        const lineMap = {}
        for (const it of items) {
          const y = Math.round(it.transform[5])
          if (!lineMap[y]) lineMap[y] = []
          lineMap[y].push({ x: it.transform[4], text: it.str.trim() })
        }
        const lines = Object.entries(lineMap)
          .sort(([a], [b]) => Number(b) - Number(a))
          .map(([, cells]) => cells.sort((a, b) => a.x - b.x).map(c => c.text))
        allLines.push(...lines)
      }

      // Detect Staff POS Sales Summary format
      const allText = allLines.map(c => c.join(' ')).join(' ')
      const isStaffPOS = /staff pos|pos sales/i.test(allText)

      if (isStaffPOS) {
        // Staff POS format: "CODE NAME | Qty | Sales R | %Sales | Cost R | Profit R"
        // Track sections to skip non-food categories
        const SKIP_SECTIONS = /^(soft drink|tea|sweets|miscellaneous|not grouped)$/i
        const SECTION_HEADERS = /^(bakery|kitchen|sandwich|soft drink|tea|sweets & chips|miscellaneous|not grouped)$/i
        let skipSection = true // skip unnamed first section (matches, plastic, etc.)

        for (const cells of allLines) {
          if (cells.length === 0) continue
          const joined = cells.join(' ').trim()

          // Check for section header (usually single cell or first cell is a section name)
          if (cells.length <= 2 && SECTION_HEADERS.test(cells[0])) {
            skipSection = SKIP_SECTIONS.test(cells[0])
            continue
          }
          if (skipSection) continue

          // Skip page headers, column headers, subtotal rows
          if (/^(group|page|staff pos|makhado|for the period|sales r)/i.test(cells[0])) continue
          if (cells.length < 2) continue
          // Skip rows where first cell is purely numeric (subtotals)
          if (/^\d[\d,.]*$/.test(cells[0])) continue

          const name = cells[0]
          // Find Qty: first integer-like numeric value after the name
          let qty = null
          for (let j = 1; j < cells.length; j++) {
            const cleaned = cells[j].replace(/[,\s]/g, '')
            if (cleaned.includes('%')) continue
            const v = Number(cleaned)
            if (!isNaN(v) && v > 0 && Number.isInteger(v)) {
              qty = v
              break
            }
          }

          if (name && qty !== null) {
            rows.push({ name, qty })
          }
        }
      } else {
        // Generic PDF: take first cell as name, find last numeric as qty
        for (const cells of allLines) {
          if (cells.length < 2) continue
          const name = cells[0]
          let qty = null
          for (let j = cells.length - 1; j >= 1; j--) {
            const v = Number(cells[j].replace(/[,\s]/g, ''))
            if (!isNaN(v) && v > 0) { qty = v; break }
          }
          if (name && qty !== null && !/total|sum|date|product|item/i.test(name)) {
            rows.push({ name, qty })
          }
        }
      }
    } else {
      addToast('Unsupported file format. Use .xlsx, .xls, or .pdf', 'error')
      return
    }

    if (rows.length === 0) {
      addToast('No product data found in file', 'warning')
      return
    }

    // Match products using fuzzy matching
    const unmatched = []
    let matched = 0
    for (const row of rows) {
      const product = matchProduct(row.name)
      if (product) {
        setValues(prev => ({
          ...prev,
          [product.code]: {
            ...prev[product.code],
            reportedSales: (Number(prev[product.code]?.reportedSales) || 0) + row.qty
          }
        }))
        matched++
      } else {
        unmatched.push(row.name)
      }
    }

    setUploadUnmatched(unmatched)
    addToast(
      `Populated ${matched} product(s)` + (unmatched.length > 0 ? ` — ${unmatched.length} unmatched` : ''),
      unmatched.length > 0 ? 'warning' : 'success'
    )
  }

  // --- Pull sales data from Yoco API (Yahweh's only) ---
  const checkYocoStatus = useCallback(async () => {
    if (dept !== 'yawehs') return
    try {
      const res = await fetch('/api/yoco/status')
      const data = await res.json()
      setYocoConnected(data.connected)
    } catch {
      setYocoConnected(false)
    }
  }, [dept])

  // Check Yoco connection status when component mounts (yawehs only)
  useState(() => { checkYocoStatus() })

  const handleYocoPull = async () => {
    setYocoPulling(true)
    setUploadUnmatched([])
    try {
      const res = await authedFetch(`/api/yoco/transactions?date=${date}`)
      const data = await res.json()

      if (!res.ok) {
        if (data.error === 'not_connected') {
          setYocoConnected(false)
          addToast('Yoco not connected. Click "Connect Yoco" first.', 'error')
        } else {
          addToast(data.message || data.error || 'Failed to pull Yoco data', 'error')
        }
        return
      }

      if (!data.items || data.items.length === 0) {
        addToast(`No transactions found for ${date}`, 'warning')
        return
      }

      // Use the same matching logic as file upload
      const unmatched = []
      let matched = 0
      for (const row of data.items) {
        const product = matchProduct(row.name)
        if (product) {
          setValues(prev => ({
            ...prev,
            [product.code]: {
              ...prev[product.code],
              reportedSales: (Number(prev[product.code]?.reportedSales) || 0) + row.qty
            }
          }))
          matched++
        } else {
          unmatched.push(row.name)
        }
      }

      setUploadUnmatched(unmatched)
      addToast(
        `Pulled ${data.total_orders} orders, matched ${matched} product(s)` +
        (unmatched.length > 0 ? ` — ${unmatched.length} unmatched` : ''),
        unmatched.length > 0 ? 'warning' : 'success'
      )
    } catch (err) {
      addToast('Failed to connect to Yoco API', 'error')
    } finally {
      setYocoPulling(false)
    }
  }

  // Location selection view
  if (!pointId) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-4 flex-wrap">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Date</label>
            <DateInput value={date} onChange={setDate} />
          </div>
        </div>

        <p className="text-sm text-gray-400">Select a distribution point:</p>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {points.map(p => {
            const hasData = getDistributionLogs(date, p.id, dept).length > 0
            return (
              <button
                key={p.id}
                onClick={() => selectPoint(p.id)}
                className={`relative flex flex-col items-center gap-2 p-4 rounded-lg border-2 text-sm font-medium transition-all hover:border-brand-500 hover:bg-brand-950/20 ${
                  hasData ? 'border-green-700 bg-green-950/10' : 'border-gray-700 bg-gray-800/30'
                }`}
              >
                <MapPin size={20} className={hasData ? 'text-green-400' : 'text-gray-500'} />
                <span className="text-white">{p.name}</span>
                {hasData && <span className="text-[10px] text-green-400">Data entered</span>}
                {p.custom && (
                  <button
                    onClick={(e) => { e.stopPropagation(); handleRemovePoint(p.id) }}
                    className="absolute top-1 right-1 text-gray-600 hover:text-red-400"
                  >
                    <X size={12} />
                  </button>
                )}
              </button>
            )
          })}

          {/* Add new point button */}
          <button
            onClick={() => setShowAddPoint(true)}
            className="flex flex-col items-center gap-2 p-4 rounded-lg border-2 border-dashed border-gray-700 text-sm text-gray-500 hover:border-amber-600 hover:text-amber-400 transition-all"
          >
            <Plus size={20} />
            <span>Add Point</span>
          </button>
        </div>

        {showAddPoint && (
          <div className="flex items-center gap-2 bg-gray-800/50 rounded p-3">
            <input
              value={newPointName}
              onChange={e => setNewPointName(e.target.value)}
              className="bg-gray-900 border border-gray-700 rounded px-3 py-1.5 text-sm text-white flex-1"
              placeholder="Distribution point name"
              autoFocus
              onKeyDown={e => e.key === 'Enter' && handleAddPoint()}
            />
            <button onClick={handleAddPoint} className="bg-brand-600 text-white px-3 py-1.5 rounded text-sm">Add</button>
            <button onClick={() => setShowAddPoint(false)} className="text-gray-500 hover:text-white"><X size={16} /></button>
          </div>
        )}
      </div>
    )
  }

  // Data entry view for selected point
  const pointName = points.find(p => p.id === pointId)?.name || pointId
  const hasData = Object.values(values).some(v => v && (v.sent || v.reportedSurplus || v.damages))

  // ─── View Mode ───
  if (viewMode && hasData) {
    const filledProducts = products.filter(p => {
      const v = values[p.code]
      return v && (v.sent || v.reportedSurplus || v.damages)
    })

    return (
      <div className="space-y-4">
        <div className="flex items-center gap-4 flex-wrap">
          <button
            onClick={() => { setPointId(''); setValues({}); setUploadUnmatched([]); setViewMode(false) }}
            className="flex items-center gap-1 bg-gray-800 hover:bg-gray-700 text-gray-300 px-3 py-2 rounded text-sm"
          >
            <ArrowLeft size={14} /> Back
          </button>
          <div className="flex items-center gap-2">
            <MapPin size={16} className="text-brand-400" />
            <span className="text-sm font-medium text-white">{pointName}</span>
          </div>
          <DateInput value={date} onChange={(d) => { setDate(d); setValues({}); setUploadUnmatched([]); setViewMode(false); setTimeout(() => selectPoint(pointId), 0) }} />
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-green-400 text-sm">
            <Eye size={16} /> Saved Distribution — {filledProducts.length} items
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setViewMode(false)}
              className="flex items-center gap-1 bg-gray-800 hover:bg-gray-700 text-white px-3 py-1.5 rounded text-xs"
            >
              <Edit3 size={12} /> Edit
            </button>
            <button
              onClick={handleDelete}
              className="flex items-center gap-1 bg-gray-800 hover:bg-red-900/50 text-red-400 px-3 py-1.5 rounded text-xs"
            >
              <Trash2 size={12} /> Delete
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-gray-400 text-xs border-b border-gray-800">
                <th className="text-left py-2 px-2">Product</th>
                <th className="text-right py-2 px-1">Sent</th>
                <th className="text-right py-2 px-1">Proj.(R)</th>
                <th className="text-right py-2 px-1">Surplus</th>
                <th className="text-right py-2 px-1">Sold</th>
                <th className="text-right py-2 px-1">Surp.Val</th>
                <th className="text-right py-2 px-1">Dmg</th>
              </tr>
            </thead>
            <tbody>
              {filledProducts.map(p => {
                const v = values[p.code] || {}
                const sent = Number(v.sent) || 0
                const surplus = Number(v.reportedSurplus) || 0
                // Use the frozen unitPrice saved onto the record so
                // historical rows render at the price actually charged —
                // not today's base price (which may have moved).
                const frozen = Number(v.frozenUnitPrice)
                const basePrice = priceFor(p, dept)
                const price = Number.isFinite(frozen) && frozen > 0 ? frozen : basePrice
                const isSitePrice = Number.isFinite(frozen) && frozen > 0 && Math.abs(frozen - basePrice) > 0.01
                const projected = sent * price
                const qtySold = Math.max(0, sent - surplus)
                const surplusValue = surplus * price
                return (
                  <tr key={p.code} className="border-b border-gray-800/50">
                    <td className="py-2 px-2">
                      <span className="text-sm">{p.name}</span>
                      <span className="text-gray-500 font-mono text-[10px] ml-2">{p.code}</span>
                      {isSitePrice && (
                        <span
                          className="ml-2 inline-block text-[9px] px-1.5 py-0.5 rounded bg-amber-900/50 text-amber-300"
                          title="Custom price set for this site"
                        >Site price</span>
                      )}
                    </td>
                    <td className="py-2 px-1 text-right text-sm">{sent || '—'}</td>
                    <td className="py-2 px-1 text-right text-xs text-gray-500">{sent > 0 ? `R${projected}` : '—'}</td>
                    <td className="py-2 px-1 text-right text-sm">{surplus || '—'}</td>
                    <td className="py-2 px-1 text-right text-xs text-gray-500">{sent > 0 ? qtySold : '—'}</td>
                    <td className="py-2 px-1 text-right text-xs text-gray-500">{surplus > 0 ? `R${surplusValue}` : '—'}</td>
                    <td className="py-2 px-1 text-right text-sm text-red-400">{v.damages || '—'}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Header with back button */}
      <div className="flex items-center gap-4 flex-wrap">
        <button
          onClick={() => { setPointId(''); setValues({}); setUploadUnmatched([]); setViewMode(false) }}
          className="flex items-center gap-1 bg-gray-800 hover:bg-gray-700 text-gray-300 px-3 py-2 rounded text-sm"
        >
          <ArrowLeft size={14} /> Back
        </button>
        <div className="flex items-center gap-2">
          <MapPin size={16} className="text-brand-400" />
          <span className="text-sm font-medium text-white">{pointName}</span>
        </div>
        <div>
          <DateInput value={date} onChange={(d) => { setDate(d); setValues({}); setUploadUnmatched([]); setViewMode(false) }} />
        </div>
        <button
          onClick={loadExisting}
          className="flex items-center gap-1 bg-gray-800 hover:bg-gray-700 text-gray-300 px-3 py-2 rounded text-xs"
        >
          <Download size={12} /> Load saved
        </button>
      </div>


      {/* Search */}
      <div className="relative mb-2">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
        <input
          type="text"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          placeholder="Search products..."
          className="w-full bg-gray-900 border border-gray-700 rounded pl-9 pr-3 py-1.5 text-sm text-white placeholder:text-gray-500"
        />
      </div>

      <div className="overflow-x-auto max-h-[65vh] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10 bg-gray-950">
            <tr className="text-gray-400 text-xs border-b border-gray-800">
              <th className="text-left py-2 px-1">Product</th>
              <th className="text-right py-2 px-1">Sent</th>
              <th
                className="text-right py-2 px-1 text-gray-500"
                title="Expected collection = (sent − surplus) × unit price"
              >Proj.(R)</th>
              <th className="text-right py-2 px-1">Surplus</th>
              <th className="text-right py-2 px-1 text-gray-500">Sold</th>
              <th className="text-right py-2 px-1 text-gray-500">Surp.Val</th>
              <th className="text-right py-2 px-1">Dmg</th>
            </tr>
          </thead>
          <tbody>
            {(searchTerm
              ? products.filter(p =>
                  p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  p.code.toLowerCase().includes(searchTerm.toLowerCase())
                )
              : products
            ).map((p, i) => {
              const v = values[p.code] || {}
              const hasValues = v.sent || v.reportedSurplus || v.damages
              const sent = Number(v.sent) || 0
              const surplus = Number(v.reportedSurplus) || 0
              const qtySold = Math.max(0, sent - surplus)
              // Projected = expected collection = sold × price (NOT sent × price).
              // Sites return surplus, so they only owe for what was sold.
              // Matches getBakeryProjectedForSite + the Daily Collections total.
              const rowPrice = priceForRow(p)
              const basePrice = priceFor(p, dept)
              const isSiteOverride = pointId && Math.abs(rowPrice - basePrice) > 0.01
              const projected = qtySold * rowPrice
              const surplusValue = surplus * rowPrice
              const surplusExceeds = surplus > sent && sent > 0
              return (
                <tr key={p.code} className={`border-b border-gray-800/50 ${surplusExceeds ? 'bg-red-950/20' : hasValues ? 'bg-gray-800/20' : i % 2 === 0 ? '' : 'bg-gray-900/30'}`}>
                  <td className="py-1.5 px-1">
                    <div className="text-white text-xs font-medium leading-tight">{p.name}</div>
                    <div className="text-gray-500 font-mono text-[10px]">
                      {p.code}{' '}
                      <span className={isSiteOverride ? 'text-amber-400' : 'text-gray-600'}>
                        @ R{rowPrice || 0}
                      </span>
                      {isSiteOverride
                        ? <span className="text-amber-500 text-[9px] ml-1">site</span>
                        : <span className="text-gray-600 text-[9px] ml-1">base</span>}
                      {sent > 0 && (
                        <span className="text-gray-500 ml-2">= R{(sent * rowPrice).toFixed(2)}</span>
                      )}
                    </div>
                  </td>
                  <td className="py-1.5 px-1">
                    <ArithmeticInput
                      min="0"
                      value={v.sent ?? ''}
                      onChange={(val) => updateField(p.code, 'sent', val == null ? '' : val)}
                      className="w-full bg-gray-900 border border-gray-700 rounded px-2 py-1 text-xs text-white text-right focus:border-brand-500 focus:outline-none"
                      placeholder="0"
                    />
                  </td>
                  <td className="py-1.5 px-1 text-right text-xs text-gray-500">{sent > 0 ? `R${projected}` : ''}</td>
                  <td className="py-1.5 px-1">
                    <ArithmeticInput
                      min="0"
                      value={v.reportedSurplus ?? ''}
                      onChange={(val) => updateField(p.code, 'reportedSurplus', val == null ? '' : val)}
                      className={`w-full bg-gray-900 border rounded px-2 py-1 text-xs text-white text-right focus:border-brand-500 focus:outline-none ${surplusExceeds ? 'border-red-600' : 'border-gray-700'}`}
                      placeholder="0"
                    />
                  </td>
                  <td className="py-1.5 px-1 text-right text-xs text-gray-500">{sent > 0 ? qtySold : ''}</td>
                  <td className="py-1.5 px-1 text-right text-xs text-gray-500">{surplus > 0 ? `R${surplusValue}` : ''}</td>
                  <td className="py-1.5 px-1">
                    <ArithmeticInput
                      min="0"
                      value={v.damages ?? ''}
                      onChange={(val) => updateField(p.code, 'damages', val == null ? '' : val)}
                      className="w-full bg-gray-900 border border-gray-700 rounded px-2 py-1 text-xs text-white text-right focus:border-brand-500 focus:outline-none"
                      placeholder="0"
                    />
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Site total — sum of qtySent × sitePrice across all entries.
          Updates live as quantities change so staff see the running value
          before saving. This is the gross distribution value, not the
          collection-projected (which subtracts surplus). */}
      {pointId && (() => {
        const rows = products
        let total = 0
        let overrideCount = 0
        for (const p of rows) {
          const v = values[p.code]
          if (!v) continue
          const sent = Number(v.sent) || 0
          if (sent <= 0) continue
          const price = priceForRow(p)
          const basePrice = priceFor(p, dept)
          if (Math.abs(price - basePrice) > 0.01) overrideCount++
          total += sent * price
        }
        return (
          <div className="flex items-center justify-between bg-gray-900/70 border border-gray-800 rounded-lg px-4 py-3">
            <div className="text-xs text-gray-400">
              Site total (sent × price)
              {overrideCount > 0 && (
                <span className="ml-2 text-amber-400">· {overrideCount} site-specific price{overrideCount === 1 ? '' : 's'}</span>
              )}
            </div>
            <div className="text-lg font-semibold text-white">R{total.toFixed(2)}</div>
          </div>
        )
      })()}

      <button
        onClick={handleSave}
        className="flex items-center gap-2 bg-brand-600 hover:bg-brand-500 text-white px-6 py-2.5 rounded-lg text-sm font-medium"
      >
        <Save size={16} /> Save Distribution — {pointName}
      </button>
    </div>
  )
}

// --- Summary View (read-only, aggregates across all distribution points) ---
function BakerySummaryView({ date: dateProp }) {
  const { dept, products, getDistPoints } = useDeptConfig()
  const today = new Date().toISOString().split('T')[0]
  const [date, setDate] = useState(dateProp || today)
  useEffect(() => { if (dateProp) setDate(dateProp) }, [dateProp])

  const points = getDistPoints()
  const distribution = getDistributionLogs(date, undefined, dept)

  // Aggregate per product across all distribution points
  const productSummary = useMemo(() => {
    const summary = {}
    for (const p of products) {
      const logs = distribution.filter(l => l.productCode === p.code)
      if (logs.length === 0) continue
      const sent = logs.reduce((s, l) => s + (Number(l.qtySent) || 0), 0)
      const reportedSales = logs.reduce((s, l) => s + (Number(l.qtySold) || 0), 0)
      const reportedSurplus = logs.reduce((s, l) => s + (Number(l.reportedSurplus) || 0), 0)
      const defaultSurplus = sent - reportedSales
      const damages = logs.reduce((s, l) => s + (Number(l.damages) || 0), 0)
      summary[p.code] = {
        name: p.name,
        unitPrice: priceFor(p, dept),
        sent,
        reportedSales,
        reportedSurplus,
        defaultSurplus,
        damages,
      }
    }
    return summary
  }, [distribution, products])

  const entries = Object.entries(productSummary)
  const totals = entries.reduce((acc, [, s]) => ({
    sent: acc.sent + s.sent,
    reportedSales: acc.reportedSales + s.reportedSales,
    reportedSurplus: acc.reportedSurplus + s.reportedSurplus,
    defaultSurplus: acc.defaultSurplus + s.defaultSurplus,
    damages: acc.damages + s.damages,
  }), { sent: 0, reportedSales: 0, reportedSurplus: 0, defaultSurplus: 0, damages: 0 })

  const hasData = entries.length > 0

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <div>
          <label className="block text-xs text-gray-400 mb-1">Date</label>
          <DateInput value={date} onChange={setDate} />
        </div>
      </div>

      {!hasData && (
        <div className="text-center py-12 text-gray-600 text-sm">
          No distribution data for this date. Enter data in the Distribution tab first.
        </div>
      )}

      {hasData && (
        <>
          {/* Totals */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {[
              { label: 'Total Sent', value: totals.sent, color: 'text-purple-400' },
              { label: 'Total Rep. Sales', value: totals.reportedSales, color: 'text-green-400' },
              { label: 'Total Rep. Surplus', value: totals.reportedSurplus, color: 'text-amber-400' },
              { label: 'Total Def. Surplus', value: totals.defaultSurplus, color: 'text-blue-400' },
              { label: 'Total Damages', value: totals.damages, color: 'text-red-400' },
            ].map(s => (
              <div key={s.label} className="bg-gray-800/50 rounded-lg p-3 text-center">
                <p className="text-xs text-gray-500 mb-1">{s.label}</p>
                <p className={`text-lg font-bold ${s.color}`}>{s.value}</p>
              </div>
            ))}
          </div>

          {/* Per-product breakdown */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-gray-400 text-xs border-b border-gray-800">
                  <th className="text-left py-2 px-2">Code</th>
                  <th className="text-left py-2 px-2">Product</th>
                  <th className="text-right py-2 px-2">Sent</th>
                  <th className="text-right py-2 px-2">Rep. Sales</th>
                  <th className="text-right py-2 px-2">Rep. Surplus</th>
                  <th className="text-right py-2 px-2">Def. Surplus</th>
                  <th className="text-right py-2 px-2">Damages</th>
                </tr>
              </thead>
              <tbody>
                {entries.map(([code, s]) => (
                  <tr key={code} className="border-b border-gray-800/50">
                    <td className="py-1.5 px-2 font-mono text-xs text-gray-500">{code}</td>
                    <td className="py-1.5 px-2">{s.name}</td>
                    <td className="py-1.5 px-2 text-right text-purple-400">{s.sent || '—'}</td>
                    <td className="py-1.5 px-2 text-right text-green-400">{s.reportedSales || '—'}</td>
                    <td className="py-1.5 px-2 text-right text-amber-400">{s.reportedSurplus || '—'}</td>
                    <td className={`py-1.5 px-2 text-right ${s.defaultSurplus < 0 ? 'text-red-400' : 'text-blue-400'}`}>
                      {s.defaultSurplus}
                    </td>
                    <td className="py-1.5 px-2 text-right text-red-400">{s.damages || '—'}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-gray-700 font-medium">
                  <td colSpan="2" className="py-2 px-2 text-gray-400">Totals</td>
                  <td className="py-2 px-2 text-right text-purple-400">{totals.sent}</td>
                  <td className="py-2 px-2 text-right text-green-400">{totals.reportedSales}</td>
                  <td className="py-2 px-2 text-right text-amber-400">{totals.reportedSurplus}</td>
                  <td className={`py-2 px-2 text-right ${totals.defaultSurplus < 0 ? 'text-red-400' : 'text-blue-400'}`}>{totals.defaultSurplus}</td>
                  <td className="py-2 px-2 text-right text-red-400">{totals.damages}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      )}
    </div>
  )
}

// --- Expenditure Form (Yaweh's Delight) ---
function ExpenditureForm({ onSaved }) {
  const { addToast } = useToast()
  const { dept } = useDeptConfig()
  const today = new Date().toISOString().split('T')[0]
  const [date, setDate] = useState(today)
  const [rows, setRows] = useState([{ description: '', amount: '' }])
  const [viewMode, setViewMode] = useState(false)

  const loadExisting = useCallback(() => {
    const logs = getExpenditure(date, dept)
    if (logs.length > 0) {
      setRows(logs.map(l => ({ description: l.description, amount: l.amount })))
      setViewMode(true)
    } else {
      setRows([{ description: '', amount: '' }])
      setViewMode(false)
    }
  }, [date, dept])

  useState(() => { loadExisting() })

  const updateRow = (i, field, val) => {
    setRows(prev => prev.map((r, idx) => idx === i ? { ...r, [field]: val } : r))
  }

  const addRow = () => setRows(prev => [...prev, { description: '', amount: '' }])

  const removeRow = (i) => setRows(prev => prev.filter((_, idx) => idx !== i))

  const handleSave = () => {
    const entries = rows.filter(r => r.description && Number(r.amount) > 0)
    if (entries.length === 0) {
      addToast('No expenditure entries', 'warning')
      return
    }
    saveExpenditureBatch(date, entries, dept)
    addToast(`Saved ${entries.length} expenditure entries`, 'success')
    setViewMode(true)
    onSaved?.()
  }

  const handleDelete = () => {
    if (!confirm(`Delete all expenditure entries for ${date}?`)) return
    const count = deleteExpenditureForDate(date, dept)
    addToast(`Deleted ${count} expenditure entries`, 'success')
    setRows([{ description: '', amount: '' }])
    setViewMode(false)
    onSaved?.()
  }

  const total = rows.reduce((s, r) => s + (Number(r.amount) || 0), 0)
  const hasData = rows.some(r => r.description && Number(r.amount) > 0)

  // ─── View Mode ───
  if (viewMode && hasData) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-4 flex-wrap">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Date</label>
            <DateInput value={date} onChange={d => { setDate(d); setTimeout(() => loadExisting(), 0) }} />
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-green-400 text-sm">
            <Eye size={16} /> Saved Expenditure — {rows.filter(r => r.description).length} items, R{total.toFixed(2)}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setViewMode(false)}
              className="flex items-center gap-1 bg-gray-800 hover:bg-gray-700 text-white px-3 py-1.5 rounded text-xs"
            >
              <Edit3 size={12} /> Edit
            </button>
            <button
              onClick={handleDelete}
              className="flex items-center gap-1 bg-gray-800 hover:bg-red-900/50 text-red-400 px-3 py-1.5 rounded text-xs"
            >
              <Trash2 size={12} /> Delete
            </button>
          </div>
        </div>

        <div className="space-y-1">
          {rows.filter(r => r.description).map((row, i) => (
            <div key={i} className="flex items-center justify-between bg-gray-800/50 rounded px-4 py-2">
              <span className="text-sm">{row.description}</span>
              <span className="text-sm font-medium text-white">R{Number(row.amount).toFixed(2)}</span>
            </div>
          ))}
          <div className="flex items-center justify-between bg-gray-700/50 rounded px-4 py-2 font-medium">
            <span className="text-sm text-gray-400">Total</span>
            <span className="text-sm text-white">R{total.toFixed(2)}</span>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4 flex-wrap">
        <div>
          <label className="block text-xs text-gray-400 mb-1">Date</label>
          <DateInput value={date} onChange={d => { setDate(d); setTimeout(() => loadExisting(), 0) }} />
        </div>
      </div>

      <div className="space-y-2">
        {rows.map((row, i) => (
          <div key={i} className="flex items-center gap-3 bg-gray-800/50 rounded px-4 py-2">
            <input
              value={row.description}
              onChange={e => updateRow(i, 'description', e.target.value)}
              className="bg-gray-900 border border-gray-700 rounded px-3 py-1.5 text-sm text-white flex-1"
              placeholder="Description (e.g. Flour, Electricity)"
            />
            <div className="flex items-center gap-1">
              <span className="text-gray-500 text-sm">R</span>
              <ArithmeticInput
                min="0"
                value={row.amount}
                onChange={(val) => updateRow(i, 'amount', val == null ? '' : val)}
                className="bg-gray-900 border border-gray-700 rounded px-3 py-1.5 text-sm text-white w-28 text-right"
                placeholder="0.00"
              />
            </div>
            {rows.length > 1 && (
              <button onClick={() => removeRow(i)} className="text-gray-600 hover:text-red-400">
                <Trash2 size={14} />
              </button>
            )}
          </div>
        ))}
      </div>

      <button
        onClick={addRow}
        className="flex items-center gap-1 bg-gray-800 hover:bg-gray-700 text-gray-300 px-3 py-2 rounded text-xs"
      >
        <Plus size={14} /> Add row
      </button>

      <div className="text-right text-sm text-gray-400">
        Total: <span className="text-white font-medium">R{total.toFixed(2)}</span>
      </div>

      <button
        onClick={handleSave}
        className="flex items-center gap-2 bg-brand-600 hover:bg-brand-500 text-white px-6 py-2.5 rounded-lg text-sm font-medium"
      >
        <Banknote size={16} /> Save Expenditure
      </button>
    </div>
  )
}

// --- Cash on Hand Form (Yaweh's Delight) ---
function CashOnHandForm({ onSaved }) {
  const { addToast } = useToast()
  const { dept } = useDeptConfig()
  const today = new Date().toISOString().split('T')[0]
  const [date, setDate] = useState(today)
  const [cash, setCash] = useState('')
  const [card, setCard] = useState('')
  const [viewMode, setViewMode] = useState(false)

  const loadExisting = useCallback(() => {
    const logs = getCashOnHand(date, dept)
    if (logs.length > 0) {
      setCash(logs[0].cash || '')
      setCard(logs[0].card || '')
      setViewMode(true)
    } else {
      setCash('')
      setCard('')
      setViewMode(false)
    }
  }, [date, dept])

  useState(() => { loadExisting() })

  const total = (Number(cash) || 0) + (Number(card) || 0)

  const handleSave = () => {
    if (!cash && !card) {
      addToast('Enter cash or card amount', 'warning')
      return
    }
    saveCashOnHand(date, cash, card, dept)
    addToast(`Saved cash on hand: R${total.toFixed(2)}`, 'success')
    setViewMode(true)
    onSaved?.()
  }

  const handleDelete = () => {
    if (!confirm(`Delete cash on hand for ${date}?`)) return
    const count = deleteCashOnHandForDate(date, dept)
    addToast(`Deleted ${count} cash on hand entries`, 'success')
    setCash('')
    setCard('')
    setViewMode(false)
    onSaved?.()
  }

  const hasData = (Number(cash) || 0) > 0 || (Number(card) || 0) > 0

  // ─── View Mode ───
  if (viewMode && hasData) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-4 flex-wrap">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Date</label>
            <DateInput value={date} onChange={d => { setDate(d); setTimeout(() => loadExisting(), 0) }} />
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-green-400 text-sm">
            <Eye size={16} /> Saved Cash on Hand — R{total.toFixed(2)}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setViewMode(false)}
              className="flex items-center gap-1 bg-gray-800 hover:bg-gray-700 text-white px-3 py-1.5 rounded text-xs"
            >
              <Edit3 size={12} /> Edit
            </button>
            <button
              onClick={handleDelete}
              className="flex items-center gap-1 bg-gray-800 hover:bg-red-900/50 text-red-400 px-3 py-1.5 rounded text-xs"
            >
              <Trash2 size={12} /> Delete
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-gray-800/50 rounded-lg p-4">
            <label className="block text-xs text-gray-400 mb-2">Cash</label>
            <p className="text-lg font-bold text-white text-right">R{(Number(cash) || 0).toFixed(2)}</p>
          </div>
          <div className="bg-gray-800/50 rounded-lg p-4">
            <label className="block text-xs text-gray-400 mb-2">Card</label>
            <p className="text-lg font-bold text-white text-right">R{(Number(card) || 0).toFixed(2)}</p>
          </div>
          <div className="bg-gray-800/50 rounded-lg p-4">
            <label className="block text-xs text-gray-400 mb-2">Total</label>
            <p className="text-xl font-bold text-green-400 text-right">R{total.toFixed(2)}</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4 flex-wrap">
        <div>
          <label className="block text-xs text-gray-400 mb-1">Date</label>
          <DateInput value={date} onChange={d => { setDate(d); setTimeout(() => loadExisting(), 0) }} />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-gray-800/50 rounded-lg p-4">
          <label className="block text-xs text-gray-400 mb-2">Cash</label>
          <div className="flex items-center gap-1">
            <span className="text-gray-500">R</span>
            <ArithmeticInput
              min="0"
              value={cash}
              onChange={(val) => setCash(val == null ? '' : val)}
              className="bg-gray-900 border border-gray-700 rounded px-3 py-2 text-sm text-white w-full text-right"
              placeholder="0.00"
            />
          </div>
        </div>
        <div className="bg-gray-800/50 rounded-lg p-4">
          <label className="block text-xs text-gray-400 mb-2">Card</label>
          <div className="flex items-center gap-1">
            <span className="text-gray-500">R</span>
            <ArithmeticInput
              min="0"
              value={card}
              onChange={(val) => setCard(val == null ? '' : val)}
              className="bg-gray-900 border border-gray-700 rounded px-3 py-2 text-sm text-white w-full text-right"
              placeholder="0.00"
            />
          </div>
        </div>
        <div className="bg-gray-800/50 rounded-lg p-4">
          <label className="block text-xs text-gray-400 mb-2">Total</label>
          <p className="text-xl font-bold text-green-400 text-right">R{total.toFixed(2)}</p>
        </div>
      </div>

      <button
        onClick={handleSave}
        className="flex items-center gap-2 bg-brand-600 hover:bg-brand-500 text-white px-6 py-2.5 rounded-lg text-sm font-medium"
      >
        <Wallet size={16} /> Save Cash on Hand
      </button>
    </div>
  )
}

// --- Shop Sales Form (manual entry + Yoco upload for the shop location) ---
function ShopSalesForm({ date: dateProp, onSaved }) {
  const { addToast } = useToast()
  const { dept, products } = useDeptConfig()
  const [date, setDate] = useState(dateProp)
  useEffect(() => setDate(dateProp), [dateProp])
  const [values, setValues] = useState({})
  const [viewMode, setViewMode] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [entryMode, setEntryMode] = useState('manual') // 'manual' | 'upload'
  const fileInputRef = useRef(null)

  const loadExisting = useCallback(() => {
    const sales = getSalesTransactions(dept, date).filter(s => s.source === 'shop')
    if (sales.length > 0) {
      const v = {}
      for (const s of sales) {
        if (v[s.productCode]) {
          v[s.productCode] = (Number(v[s.productCode]) || 0) + (Number(s.qtySold) || 0)
        } else {
          v[s.productCode] = s.qtySold
        }
      }
      setValues(v)
      setViewMode(true)
    } else {
      setValues({})
      setViewMode(false)
    }
  }, [date, dept])

  useState(() => { loadExisting() })

  const updateValue = (code, qty) => {
    setValues(prev => ({ ...prev, [code]: qty }))
  }

  const handleSave = async () => {
    const entries = Object.entries(values)
      .filter(([, qty]) => qty !== '' && Number(qty) > 0)
      .map(([productCode, qtySold]) => {
        const prod = products.find(p => p.code === productCode)
        return {
          date,
          productCode,
          productName: prod?.name || productCode,
          category: prod?.category || '',
          unitPrice: priceFor(prod, dept),
          qtySold: Number(qtySold),
          salesRands: Number(qtySold) * priceFor(prod, dept),
          source: 'shop',
        }
      })

    if (entries.length === 0) {
      addToast('No sales entered', 'warning')
      return
    }

    // Delete only shop-sourced sales for this date, then save fresh
    await deleteSalesForDate(dept, date, 'shop')
    saveSalesBatch(dept, entries)
    addToast(`Saved ${entries.length} shop sales for ${date}`, 'success')
    setViewMode(true)
    onSaved?.()
  }

  const handleDelete = async () => {
    if (!confirm(`Delete all shop sales for ${date}?`)) return
    const count = await deleteSalesForDate(dept, date, 'shop')
    addToast(`Deleted ${count} shop sales entries`, 'success')
    setValues({})
    setViewMode(false)
    onSaved?.()
  }

  // Yoco file upload handler
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (fileInputRef.current) fileInputRef.current.value = ''

    const XLSX = await import('xlsx')
    const data = await file.arrayBuffer()
    const wb = XLSX.read(data, { cellDates: true })
    const ws = wb.Sheets[wb.SheetNames[0]]
    const json = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '', raw: true })

    const headerRow = json[0] || []
    const headerLower = headerRow.map(h => String(h).toLowerCase().trim())
    const itemsColIdx = headerLower.indexOf('items')
    const statusColIdx = headerLower.indexOf('status')
    const dateColIdx = headerLower.indexOf('date')

    if (itemsColIdx < 0) {
      addToast('Could not find "Items" column in file', 'error')
      return
    }

    const aggregated = {}
    for (let i = 1; i < json.length; i++) {
      const row = json[i]
      if (statusColIdx >= 0) {
        const status = String(row[statusColIdx] || '').trim().toLowerCase()
        if (status && status !== 'approved') continue
      }
      // Filter to selected date if file has dates
      if (dateColIdx >= 0 && row[dateColIdx]) {
        const raw = row[dateColIdx]
        let rowDate = date
        if (raw instanceof Date) {
          const y = raw.getFullYear(), m = String(raw.getMonth() + 1).padStart(2, '0'), d = String(raw.getDate()).padStart(2, '0')
          rowDate = `${y}-${m}-${d}`
        } else {
          rowDate = String(raw).trim().replace(/\//g, '-')
          if (/^\d{2}-\d{2}-\d{4}$/.test(rowDate)) {
            const [dd, mm, yyyy] = rowDate.split('-')
            rowDate = `${yyyy}-${mm}-${dd}`
          }
        }
        if (rowDate !== date) continue
      }
      const itemsStr = String(row[itemsColIdx] || '').trim()
      if (!itemsStr) continue
      const parts = itemsStr.split(',').map(s => s.trim()).filter(Boolean)
      for (const part of parts) {
        const m = part.match(/^(\d+)\s*[x×]\s*(.+)$/i)
        const qty = m ? Number(m[1]) : 1
        const name = m ? m[2].trim() : part.trim()
        const norm = name.toLowerCase()
        const prod = products.find(p => p.name.toLowerCase() === norm || p.code.toLowerCase() === norm)
        if (prod) {
          aggregated[prod.code] = (aggregated[prod.code] || 0) + qty
        }
      }
    }

    const matched = Object.keys(aggregated).length
    if (matched === 0) {
      addToast('No products matched from upload', 'warning')
      return
    }

    setValues(prev => {
      const v = { ...prev }
      for (const [code, qty] of Object.entries(aggregated)) {
        v[code] = (Number(v[code]) || 0) + qty
      }
      return v
    })
    addToast(`Matched ${matched} products from upload`, 'success')
    setEntryMode('manual') // switch to manual to see/edit the values
  }

  const filteredProducts = searchTerm
    ? products.filter(p => p.name.toLowerCase().includes(searchTerm.toLowerCase()) || p.code.toLowerCase().includes(searchTerm.toLowerCase()))
    : products

  const hasData = Object.values(values).some(v => v !== '' && Number(v) > 0)

  if (viewMode && hasData) {
    const filledProducts = products.filter(p => values[p.code] && Number(values[p.code]) > 0)
    const totalRevenue = filledProducts.reduce((s, p) => s + (Number(values[p.code]) * priceFor(p, dept)), 0)
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-4 flex-wrap">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Date</label>
            <DateInput value={date} onChange={d => { setDate(d); setTimeout(() => loadExisting(), 0) }} />
          </div>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-green-400 text-sm">
            <Eye size={16} /> Saved Shop Sales — {filledProducts.length} items (R{totalRevenue.toFixed(0)})
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setViewMode(false)} className="flex items-center gap-1 bg-gray-800 hover:bg-gray-700 text-white px-3 py-1.5 rounded text-xs">
              <Edit3 size={12} /> Edit
            </button>
            <button onClick={handleDelete} className="flex items-center gap-1 bg-gray-800 hover:bg-red-900/50 text-red-400 px-3 py-1.5 rounded text-xs">
              <Trash2 size={12} /> Delete
            </button>
          </div>
        </div>
        <div className="space-y-1 max-h-[40vh] overflow-y-auto">
          {filledProducts.map(p => (
            <div key={p.code} className="flex items-center justify-between bg-gray-800/50 rounded px-4 py-1.5">
              <div className="flex items-center gap-3">
                <span className="font-mono text-xs text-gray-500">{p.code}</span>
                <span className="text-sm">{p.name}</span>
              </div>
              <span className="text-sm font-medium text-white">{values[p.code]} × R{priceFor(p, dept) || 0}</span>
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4 flex-wrap">
        <div>
          <label className="block text-xs text-gray-400 mb-1">Date</label>
          <DateInput value={date} onChange={d => { setDate(d); setTimeout(() => loadExisting(), 0) }} />
        </div>
        <div className="flex gap-1">
          <button
            onClick={() => setEntryMode('manual')}
            className={`px-3 py-1.5 rounded text-xs font-medium ${entryMode === 'manual' ? 'bg-brand-700 text-white' : 'bg-gray-800 text-gray-400'}`}
          >
            Manual
          </button>
          <button
            onClick={() => setEntryMode('upload')}
            className={`px-3 py-1.5 rounded text-xs font-medium ${entryMode === 'upload' ? 'bg-brand-700 text-white' : 'bg-gray-800 text-gray-400'}`}
          >
            Upload
          </button>
        </div>
      </div>

      {entryMode === 'upload' && (
        <div className="border border-dashed border-gray-700 rounded-lg p-6 text-center">
          <Upload size={24} className="mx-auto text-gray-500 mb-2" />
          <p className="text-sm text-gray-400 mb-3">Upload Yoco transaction file (.xlsx)</p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileUpload}
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            className="bg-gray-800 hover:bg-gray-700 text-white px-4 py-2 rounded text-sm"
          >
            Choose File
          </button>
        </div>
      )}

      {entryMode === 'manual' && (
        <>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <input
              type="text"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search products..."
              className="w-full bg-gray-900 border border-gray-700 rounded pl-9 pr-3 py-1.5 text-sm text-white placeholder:text-gray-500"
            />
          </div>
          <div className="overflow-x-auto max-h-[50vh] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10 bg-gray-950">
                <tr className="text-gray-400 text-xs border-b border-gray-800">
                  <th className="text-left py-2 px-2">Product</th>
                  <th className="text-right py-2 px-2 w-24">Qty Sold</th>
                </tr>
              </thead>
              <tbody>
                {filteredProducts.map((p, i) => (
                  <tr key={p.code} className={`border-b border-gray-800/50 ${values[p.code] && Number(values[p.code]) > 0 ? 'bg-gray-800/20' : i % 2 === 0 ? '' : 'bg-gray-900/30'}`}>
                    <td className="py-1.5 px-2">
                      <div className="text-white text-xs font-medium">{p.name}</div>
                      <div className="text-gray-500 font-mono text-[10px]">{p.code} · R{priceFor(p, dept) || '—'}</div>
                    </td>
                    <td className="py-1.5 px-2">
                      <ArithmeticInput
                        min="0"
                        value={values[p.code] ?? ''}
                        onChange={(val) => updateValue(p.code, val == null ? '' : val)}
                        className="w-full bg-gray-900 border border-gray-700 rounded px-2 py-1.5 text-xs text-white text-right focus:border-brand-500 focus:outline-none"
                        placeholder="0"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <button
        onClick={handleSave}
        className="flex items-center gap-2 bg-brand-600 hover:bg-brand-500 text-white px-6 py-2.5 rounded-lg text-sm font-medium"
      >
        <ShoppingBag size={16} /> Save Shop Sales
      </button>
    </div>
  )
}

// --- Location Cash Form (cash on hand per distribution point) ---
function LocationCashForm({ date: dateProp, onSaved }) {
  const { addToast } = useToast()
  const { dept, getDistPoints, addDistPoint, removeDistPoint } = useDeptConfig()
  const [date, setDate] = useState(dateProp)
  useEffect(() => setDate(dateProp), [dateProp])
  const [refreshKey, setRefreshKey] = useState(0)
  const [values, setValues] = useState({}) // { pointId: { cash, card } }
  const [showAddPoint, setShowAddPoint] = useState(false)
  const [newPointName, setNewPointName] = useState('')

  const points = useMemo(() => getDistPoints(), [refreshKey])

  const loadExisting = useCallback(() => {
    const v = {}
    for (const pt of points) {
      const records = getLocationCash(date, pt.id, dept)
      if (records.length > 0) {
        v[pt.id] = { cash: records[0].cash || '', card: records[0].card || '' }
      }
    }
    setValues(v)
  }, [date, dept, points])

  useState(() => { loadExisting() })

  const updateField = (pointId, field, val) => {
    setValues(prev => ({
      ...prev,
      [pointId]: { ...prev[pointId], [field]: val }
    }))
  }

  const handleSave = () => {
    let count = 0
    for (const pt of points) {
      const v = values[pt.id]
      if (!v) continue
      const cash = Number(v.cash) || 0
      const card = Number(v.card) || 0
      if (cash === 0 && card === 0) continue
      saveLocationCash(date, pt.id, pt.name, cash, card, dept)
      count++
    }
    if (count === 0) {
      addToast('No cash amounts entered', 'warning')
      return
    }
    addToast(`Saved cash on hand for ${count} location(s)`, 'success')
    onSaved?.()
  }

  const handleAddPoint = () => {
    if (!newPointName.trim()) return
    addDistPoint({ name: newPointName.trim(), shortName: newPointName.trim().split(' ')[0] })
    setRefreshKey(k => k + 1)
    setNewPointName('')
    setShowAddPoint(false)
    addToast(`Added location "${newPointName}"`, 'success')
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4 flex-wrap">
        <div>
          <label className="block text-xs text-gray-400 mb-1">Date</label>
          <DateInput value={date} onChange={d => { setDate(d); setTimeout(() => loadExisting(), 0) }} />
        </div>
      </div>

      {points.length === 0 && (
        <p className="text-gray-500 text-sm">No distribution points set up. Add one below.</p>
      )}

      <div className="space-y-3">
        {points.map(pt => {
          const v = values[pt.id] || {}
          const total = (Number(v.cash) || 0) + (Number(v.card) || 0)
          return (
            <div key={pt.id} className="border border-gray-800 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <MapPin size={14} className="text-brand-400" />
                <span className="text-sm font-medium text-white">{pt.name}</span>
                {total > 0 && <span className="text-xs text-green-400 ml-auto">R{total.toFixed(2)}</span>}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] text-gray-500 uppercase tracking-wide block mb-1">Cash</label>
                  <div className="flex items-center gap-1">
                    <span className="text-gray-500 text-xs">R</span>
                    <ArithmeticInput
                      min="0"
                      value={v.cash ?? ''}
                      onChange={(val) => updateField(pt.id, 'cash', val == null ? '' : val)}
                      className="w-full bg-gray-900 border border-gray-700 rounded px-2 py-1.5 text-xs text-white text-right focus:border-brand-500 focus:outline-none"
                      placeholder="0.00"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-[10px] text-gray-500 uppercase tracking-wide block mb-1">Card</label>
                  <div className="flex items-center gap-1">
                    <span className="text-gray-500 text-xs">R</span>
                    <ArithmeticInput
                      min="0"
                      value={v.card ?? ''}
                      onChange={(val) => updateField(pt.id, 'card', val == null ? '' : val)}
                      className="w-full bg-gray-900 border border-gray-700 rounded px-2 py-1.5 text-xs text-white text-right focus:border-brand-500 focus:outline-none"
                      placeholder="0.00"
                    />
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <button
          onClick={handleSave}
          className="flex items-center gap-2 bg-brand-600 hover:bg-brand-500 text-white px-6 py-2.5 rounded-lg text-sm font-medium"
        >
          <Wallet size={16} /> Save Location Cash
        </button>
        {!showAddPoint && (
          <button
            onClick={() => setShowAddPoint(true)}
            className="flex items-center gap-1 bg-gray-800 hover:bg-gray-700 text-gray-300 px-3 py-2 rounded text-xs"
          >
            <Plus size={14} /> Add Location
          </button>
        )}
        {showAddPoint && (
          <div className="flex items-center gap-2">
            <input
              value={newPointName}
              onChange={e => setNewPointName(e.target.value)}
              placeholder="Location name"
              className="bg-gray-900 border border-gray-700 rounded px-3 py-1.5 text-sm text-white"
              onKeyDown={e => e.key === 'Enter' && handleAddPoint()}
            />
            <button onClick={handleAddPoint} className="bg-brand-600 hover:bg-brand-500 text-white px-3 py-1.5 rounded text-xs">Add</button>
            <button onClick={() => setShowAddPoint(false)} className="text-gray-500 hover:text-white"><X size={14} /></button>
          </div>
        )}
      </div>
    </div>
  )
}

// --- Damages Log (standalone damaged products tracker) ---
function DamagesForm({ date, onSaved }) {
  const { addToast } = useToast()
  const { dept } = useDeptConfig()
  const products = useMemo(() => getProducts(dept), [dept])
  const [rows, setRows] = useState([])
  const [viewMode, setViewMode] = useState(false)

  const loadExisting = useCallback(() => {
    const damages = getDamages(date, dept)
    if (damages.length > 0) {
      setRows(damages.map(d => {
        const prod = products.find(p => p.code === d.productCode)
        return {
          productCode: d.productCode,
          productName: prod?.name || d.productCode,
          category: prod?.category || '',
          unitPrice: priceFor(prod, dept),
          qty: d.qty,
          lossValue: d.qty * priceFor(prod, dept),
          notes: d.notes || '',
        }
      }))
      setViewMode(true)
    } else {
      setRows([])
      setViewMode(false)
    }
  }, [date, dept, products])

  useEffect(() => { loadExisting() }, [loadExisting])

  const addRow = () => {
    setRows(prev => [...prev, { productCode: '', productName: '', category: '', unitPrice: 0, qty: '', lossValue: 0, notes: '' }])
  }

  const updateRow = (idx, field, val) => {
    setRows(prev => prev.map((r, i) => {
      if (i !== idx) return r
      const updated = { ...r, [field]: val }
      if (field === 'productCode') {
        const prod = products.find(p => p.code === val)
        if (prod) {
          updated.productName = prod.name
          updated.category = prod.category
          updated.unitPrice = priceFor(prod, dept)
          updated.lossValue = (Number(updated.qty) || 0) * priceFor(prod, dept)
        }
      }
      if (field === 'qty') {
        updated.lossValue = (Number(val) || 0) * (updated.unitPrice || 0)
      }
      return updated
    }))
  }

  const removeRow = (idx) => setRows(prev => prev.filter((_, i) => i !== idx))

  const handleSave = () => {
    const entries = rows
      .filter(r => r.productCode && Number(r.qty) > 0)
      .map(r => ({
        productCode: r.productCode,
        qty: Number(r.qty),
        lossValue: (Number(r.qty) || 0) * (r.unitPrice || 0),
        notes: r.notes || '',
      }))
    if (entries.length === 0) {
      addToast('No damage entries to save', 'warning')
      return
    }
    saveDamagesBatch(date, entries, dept)
    addToast(`Saved ${entries.length} damage entries`, 'success')
    setViewMode(true)
    onSaved?.()
  }

  const totalLoss = rows.reduce((s, r) => s + ((Number(r.qty) || 0) * (r.unitPrice || 0)), 0)

  if (viewMode && rows.length > 0) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-green-400 text-sm">
            <Eye size={16} /> Saved Damages — {rows.length} items
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setViewMode(false)} className="flex items-center gap-1 bg-gray-800 hover:bg-gray-700 text-white px-3 py-1.5 rounded text-xs">
              <Edit3 size={12} /> Edit
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-gray-400 text-xs border-b border-gray-800">
                <th className="text-left py-2 px-2">Product</th>
                <th className="text-left py-2 px-2">Category</th>
                <th className="text-right py-2 px-2">Qty</th>
                <th className="text-right py-2 px-2">Loss (R)</th>
                <th className="text-left py-2 px-2">Notes</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-b border-gray-800/50">
                  <td className="py-2 px-2">{r.productName}</td>
                  <td className="py-2 px-2 text-gray-500 capitalize text-xs">{r.category}</td>
                  <td className="py-2 px-2 text-right">{r.qty}</td>
                  <td className="py-2 px-2 text-right text-red-400">R{Math.round((Number(r.qty) || 0) * (r.unitPrice || 0))}</td>
                  <td className="py-2 px-2 text-gray-400 text-xs">{r.notes || '—'}</td>
                </tr>
              ))}
              <tr className="border-t-2 border-gray-700 font-semibold">
                <td colSpan={3} className="py-2 px-2 text-right">Total Damages</td>
                <td className="py-2 px-2 text-right text-red-400">R{Math.round(totalLoss)}</td>
                <td />
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-gray-400 text-xs border-b border-gray-800">
              <th className="text-left py-2 px-2">Product</th>
              <th className="text-left py-2 px-2">Category</th>
              <th className="text-right py-2 px-2">Qty</th>
              <th className="text-right py-2 px-2">Loss (R)</th>
              <th className="text-left py-2 px-2">Notes / Cause</th>
              <th className="py-2 px-2" />
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-b border-gray-800/50">
                <td className="py-1.5 px-2">
                  <select
                    value={r.productCode}
                    onChange={e => updateRow(i, 'productCode', e.target.value)}
                    className="bg-gray-900 border border-gray-700 rounded px-2 py-1 text-xs text-white w-full"
                  >
                    <option value="">Select product</option>
                    {['bread', 'school buns', 'yellow buns', 'white rolls', 'cream buns', 'pastry', 'other', 'sandwiches'].map(cat => {
                      const catProds = products.filter(p => p.category === cat)
                      if (catProds.length === 0) return null
                      return (
                        <optgroup key={cat} label={cat.charAt(0).toUpperCase() + cat.slice(1)}>
                          {catProds.map(p => (
                            <option key={p.code} value={p.code}>{p.name} (R{priceFor(p, dept)})</option>
                          ))}
                        </optgroup>
                      )
                    })}
                  </select>
                </td>
                <td className="py-1.5 px-2 text-gray-500 capitalize text-xs">{r.category || '—'}</td>
                <td className="py-1.5 px-2">
                  <ArithmeticInput
                    min="0"
                    value={r.qty}
                    onChange={(val) => updateRow(i, 'qty', val == null ? '' : val)}
                    className="bg-gray-900 border border-gray-700 rounded px-2 py-1 text-xs text-white text-right w-16"
                    placeholder="0"
                  />
                </td>
                <td className="py-1.5 px-2 text-right text-xs text-red-400">
                  {Number(r.qty) > 0 ? `R${Math.round((Number(r.qty) || 0) * (r.unitPrice || 0))}` : '—'}
                </td>
                <td className="py-1.5 px-2">
                  <input
                    type="text"
                    value={r.notes}
                    onChange={e => updateRow(i, 'notes', e.target.value)}
                    className="bg-gray-900 border border-gray-700 rounded px-2 py-1 text-xs text-white w-full"
                    placeholder="Cause / notes"
                  />
                </td>
                <td className="py-1.5 px-2">
                  <button onClick={() => removeRow(i)} className="text-gray-500 hover:text-red-400"><Trash2 size={12} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalLoss > 0 && (
        <div className="text-xs text-red-400 px-2">
          Total Damages: <span className="font-medium">R{Math.round(totalLoss)}</span>
        </div>
      )}

      <div className="flex items-center gap-3">
        <button onClick={addRow} className="flex items-center gap-1 bg-gray-800 hover:bg-gray-700 text-gray-300 px-3 py-2 rounded text-xs">
          <Plus size={12} /> Add Row
        </button>
        <button
          onClick={handleSave}
          className="flex items-center gap-2 bg-brand-600 hover:bg-brand-500 text-white px-6 py-2.5 rounded-lg text-sm font-medium"
        >
          <Save size={16} /> Save Damages
        </button>
      </div>
    </div>
  )
}

// --- Daily Collections (money collected per distribution site) ---
function DailyCollectionsForm({ date, onSaved }) {
  const { addToast } = useToast()
  const { dept, getDistPoints } = useDeptConfig()
  const points = useMemo(() => getDistPoints(), [])
  const [rows, setRows] = useState([])
  const [saving, setSaving] = useState(false)

  // Load existing collections + compute projected from distribution logs.
  // Projected math lives in store.js (getBakeryProjectedForSite) so the
  // ReconciliationReport, exports, and any future caller all share one
  // source of truth. The form only owns presentation + the missing-product
  // warning surfaced to staff.
  const loadData = useCallback(() => {
    const existing = getDailyCollections(dept, date)
    const newRows = points.map(pt => {
      const saved = existing.find(e => e.siteCode === pt.id)
      const projected = getBakeryProjectedForSite(dept, date, pt.id)
      const missingProducts = getMissingProductCodesForSite(dept, date, pt.id)
      if (missingProducts.length > 0) {
        console.warn(
          `[DailyCollections] Unknown product codes for site ${pt.id}:`,
          missingProducts
        )
      }
      return {
        siteCode: pt.id,
        siteName: pt.name || pt.shortName || pt.id,
        paymentType: pt.paymentType || 'cash',
        projected,
        missingProducts,
        collected: saved?.collected ?? '',
        notes: saved?.notes || '',
      }
    })
    setRows(newRows)
  }, [date, dept, points])

  useEffect(() => { loadData() }, [loadData])

  const updateRow = (siteCode, field, val) => {
    setRows(prev => prev.map(r =>
      r.siteCode === siteCode ? { ...r, [field]: val } : r
    ))
  }

  const handleSave = () => {
    setSaving(true)
    // Recompute projected fresh from the current distribution logs at the
    // moment of save. This guards against staleness when distribution data
    // is edited (in another tab, on another device, or via direct Firestore
    // edits) after the form mounted but before Save was clicked. The on-row
    // value used for live display can drift; the persisted value must not.
    const entries = rows
      .filter(r => r.collected !== '' && r.collected !== null && r.collected !== undefined)
      .map(r => ({
        siteCode: r.siteCode,
        siteName: r.siteName,
        projected: getBakeryProjectedForSite(dept, date, r.siteCode),
        collected: Number(r.collected) || 0,
        notes: r.notes,
      }))
    if (entries.length === 0) {
      addToast('No collection amounts entered', 'warning')
      setSaving(false)
      return
    }
    saveDailyCollectionsBatch(dept, date, entries)
    addToast(`Saved collections for ${entries.length} site(s)`, 'success')
    setSaving(false)
    onSaved?.()
  }

  // Compute totals
  const totals = rows.reduce((acc, r) => {
    const collected = Number(r.collected) || 0
    const diff = r.projected - collected
    return {
      projected: acc.projected + r.projected,
      collected: acc.collected + (r.collected !== '' ? collected : 0),
      short: acc.short + (diff > 0 && r.collected !== '' ? diff : 0),
    }
  }, { projected: 0, collected: 0, short: 0 })

  const shortSites = rows.filter(r => {
    const collected = Number(r.collected) || 0
    return r.collected !== '' && r.projected - collected > 0
  })

  return (
    <div className="space-y-4">
      {/* Shortage banner */}
      {shortSites.length > 0 && (
        <div className="bg-red-900/30 border border-red-800 rounded-lg p-3 flex items-center gap-3">
          <AlertTriangle size={16} className="text-red-400 shrink-0" />
          <span className="text-sm text-red-300">
            {shortSites.length} site(s) short today — R{Math.round(totals.short)} unaccounted
          </span>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-gray-400 text-xs border-b border-gray-800">
              <th className="text-left py-2 px-2">Site</th>
              <th className="text-right py-2 px-2">Projected (R)</th>
              <th className="text-right py-2 px-2">Collected (R)</th>
              <th className="text-right py-2 px-2">Difference</th>
              <th className="text-center py-2 px-2">Status</th>
              <th className="text-left py-2 px-2">Notes</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => {
              const collected = Number(r.collected) || 0
              const hasCollected = r.collected !== '' && r.collected !== null && r.collected !== undefined
              const diff = hasCollected ? r.projected - collected : null
              const isShort = diff !== null && diff > 0
              const isSurplus = diff !== null && diff < 0
              return (
                <tr key={r.siteCode} className={`border-b border-gray-800/50 ${isShort ? 'bg-red-950/20' : ''}`}>
                  <td className="py-2 px-2">
                    <div className="text-sm text-white">{r.siteName}</div>
                    <div className="text-[10px] text-gray-500">{r.paymentType}</div>
                  </td>
                  <td className="py-2 px-2 text-right text-gray-400">
                    <div className="inline-flex items-center justify-end gap-1">
                      <span>{r.projected > 0 ? `R${r.projected}` : '—'}</span>
                      {r.missingProducts && r.missingProducts.length > 0 && (
                        <AlertTriangle
                          size={12}
                          className="text-amber-400 shrink-0"
                          aria-label="Some products unrecognised — projected may be incomplete"
                          title={`Unrecognised product code(s): ${r.missingProducts.join(', ')} — projected may be incomplete`}
                        />
                      )}
                    </div>
                  </td>
                  <td className="py-2 px-2">
                    <ArithmeticInput
                      min="0"
                      value={r.collected}
                      onChange={(val) => updateRow(r.siteCode, 'collected', val == null ? '' : val)}
                      className={`w-full bg-gray-900 border rounded px-2 py-1 text-xs text-white text-right focus:outline-none focus:border-brand-500 ${isShort ? 'border-red-700' : 'border-gray-700'}`}
                      placeholder="0.00"
                    />
                  </td>
                  <td className={`py-2 px-2 text-right text-xs font-medium ${isShort ? 'text-red-400' : isSurplus ? 'text-green-400' : 'text-gray-500'}`}>
                    {diff !== null ? (diff === 0 ? '—' : `R${Math.abs(Math.round(diff))}`) : '—'}
                  </td>
                  <td className="py-2 px-2 text-center text-xs">
                    {diff === null ? '—'
                    : isShort ? <span className="text-red-400">SHORT</span>
                    : isSurplus ? <span className="text-green-400">SURPLUS</span>
                    : <span className="text-green-400">✓</span>}
                  </td>
                  <td className="py-2 px-2">
                    <input
                      type="text"
                      value={r.notes}
                      onChange={e => updateRow(r.siteCode, 'notes', e.target.value)}
                      className="w-full bg-gray-900 border border-gray-700 rounded px-2 py-1 text-xs text-white focus:outline-none focus:border-brand-500"
                      placeholder="Notes"
                    />
                  </td>
                </tr>
              )
            })}
          </tbody>
          {/* Totals footer */}
          <tfoot>
            <tr className="border-t-2 border-gray-700 font-semibold text-sm">
              <td className="py-2 px-2">Total</td>
              <td className="py-2 px-2 text-right text-gray-400">R{Math.round(totals.projected)}</td>
              <td className="py-2 px-2 text-right">R{Math.round(totals.collected)}</td>
              <td className={`py-2 px-2 text-right text-xs ${totals.short > 0 ? 'text-red-400' : 'text-green-400'}`}>
                {totals.short > 0 ? `R${Math.round(totals.short)}` : '—'}
              </td>
              <td className="py-2 px-2" />
              <td className="py-2 px-2" />
            </tr>
          </tfoot>
        </table>
      </div>

      <button
        onClick={handleSave}
        disabled={saving}
        className="flex items-center gap-2 bg-brand-600 hover:bg-brand-500 disabled:opacity-50 text-white px-6 py-2.5 rounded-lg text-sm font-medium"
      >
        <Banknote size={16} /> {saving ? 'Saving...' : 'Save Collections'}
      </button>
    </div>
  )
}

// --- Monthly Summary (read-only, aggregates collections + damages for a month) ---
function MonthlySummaryView({ date }) {
  const { dept, getDistPoints } = useDeptConfig()
  const points = useMemo(() => getDistPoints(), [])
  const products = useMemo(() => getProducts(dept), [dept])

  // Month selector: derive from passed date, allow month navigation
  const [monthStr, setMonthStr] = useState(() => (date || new Date().toISOString().split('T')[0]).slice(0, 7))
  useEffect(() => { if (date) setMonthStr(date.slice(0, 7)) }, [date])

  const monthLabel = (() => {
    const [y, m] = monthStr.split('-')
    return new Date(y, Number(m) - 1).toLocaleDateString('en-ZA', { month: 'long', year: 'numeric' })
  })()

  // Load all collections and damages for the month
  const allCollections = useMemo(() => {
    return getDailyCollections(dept).filter(r => r.date?.startsWith(monthStr))
  }, [dept, monthStr])

  const allDamages = useMemo(() => {
    return getDamages(undefined, dept).filter(r => r.date?.startsWith(monthStr))
  }, [dept, monthStr])

  // Section 1: Collection per site
  const siteSummary = useMemo(() => {
    const map = {}
    for (const c of allCollections) {
      const key = c.siteCode
      if (!map[key]) {
        const pt = points.find(p => p.id === key)
        map[key] = { siteCode: key, siteName: pt?.name || c.siteName || key, projected: 0, collected: 0, shortfall: 0, surplus: 0, shortfallDays: 0 }
      }
      map[key].projected += Number(c.projected) || 0
      map[key].collected += Number(c.collected) || 0
      const diff = (Number(c.projected) || 0) - (Number(c.collected) || 0)
      if (diff > 0) { map[key].shortfall += diff; map[key].shortfallDays++ }
      else if (diff < 0) { map[key].surplus += Math.abs(diff) }
    }
    return Object.values(map)
  }, [allCollections, points])

  // Section 2: Total damages
  const totalDamages = useMemo(() => {
    return allDamages.reduce((s, d) => {
      const prod = products.find(p => p.code === d.productCode)
      const loss = (Number(d.lossValue) || 0) || ((Number(d.qty) || 0) * priceFor(prod, dept))
      return s + loss
    }, 0)
  }, [allDamages, products])

  // Totals
  const totals = siteSummary.reduce((acc, s) => ({
    projected: acc.projected + s.projected,
    collected: acc.collected + s.collected,
    shortfall: acc.shortfall + s.shortfall,
    surplus: acc.surplus + s.surplus,
  }), { projected: 0, collected: 0, shortfall: 0, surplus: 0 })

  const netUnaccounted = totals.shortfall + totalDamages

  const prevMonth = () => {
    const [y, m] = monthStr.split('-').map(Number)
    const d = new Date(y, m - 2, 1)
    setMonthStr(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`)
  }
  const nextMonth = () => {
    const [y, m] = monthStr.split('-').map(Number)
    const d = new Date(y, m, 1)
    setMonthStr(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`)
  }

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Month selector */}
      <div className="flex items-center gap-4">
        <button onClick={prevMonth} className="text-gray-400 hover:text-white px-2 py-1 rounded bg-gray-800">←</button>
        <h2 className="text-lg font-bold text-white">{monthLabel}</h2>
        <button onClick={nextMonth} className="text-gray-400 hover:text-white px-2 py-1 rounded bg-gray-800">→</button>
      </div>

      {allCollections.length === 0 && allDamages.length === 0 ? (
        <div className="text-gray-500 text-sm py-8 text-center">No data for this month.</div>
      ) : (
        <>
          {/* Section 1: Collection per site */}
          <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
            <h3 className="text-sm font-medium mb-3">Collection Per Site</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-gray-400 text-xs border-b border-gray-800">
                    <th className="text-left py-2 px-2">Site</th>
                    <th className="text-right py-2 px-2">Projected</th>
                    <th className="text-right py-2 px-2">Collected</th>
                    <th className="text-right py-2 px-2">Shortfall</th>
                    <th className="text-right py-2 px-2">Surplus</th>
                    <th className="text-right py-2 px-2">Short Days</th>
                    <th className="text-center py-2 px-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {siteSummary.map(s => (
                    <tr key={s.siteCode} className={`border-b border-gray-800/50 ${s.shortfall > 0 ? 'bg-red-950/10' : ''}`}>
                      <td className="py-2 px-2 text-white">{s.siteName}</td>
                      <td className="py-2 px-2 text-right text-gray-400">R{Math.round(s.projected)}</td>
                      <td className="py-2 px-2 text-right">R{Math.round(s.collected)}</td>
                      <td className="py-2 px-2 text-right text-red-400">{s.shortfall > 0 ? `R${Math.round(s.shortfall)}` : '—'}</td>
                      <td className="py-2 px-2 text-right text-green-400">{s.surplus > 0 ? `R${Math.round(s.surplus)}` : '—'}</td>
                      <td className="py-2 px-2 text-right">{s.shortfallDays > 0 ? s.shortfallDays : '—'}</td>
                      <td className="py-2 px-2 text-center text-xs">
                        {s.shortfall > 0
                          ? <span className="text-amber-400">R{Math.round(s.shortfall)} unaccounted</span>
                          : <span className="text-green-400">Reconciled</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-gray-700 font-semibold">
                    <td className="py-2 px-2">Total</td>
                    <td className="py-2 px-2 text-right text-gray-400">R{Math.round(totals.projected)}</td>
                    <td className="py-2 px-2 text-right">R{Math.round(totals.collected)}</td>
                    <td className="py-2 px-2 text-right text-red-400">{totals.shortfall > 0 ? `R${Math.round(totals.shortfall)}` : '—'}</td>
                    <td className="py-2 px-2 text-right text-green-400">{totals.surplus > 0 ? `R${Math.round(totals.surplus)}` : '—'}</td>
                    <td colSpan={2} />
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Section 2: Total damages */}
          <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
            <h3 className="text-sm font-medium mb-2">Total Damages</h3>
            <p className="text-red-400 text-lg font-bold">R{Math.round(totalDamages)}</p>
            <p className="text-xs text-gray-500">{allDamages.length} damage entries this month</p>
          </div>

          {/* Section 3: Grand summary */}
          <div className="bg-gray-900 border border-gray-800 rounded-lg p-4 space-y-2">
            <h3 className="text-sm font-medium mb-3">Grand Summary — {monthLabel}</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <div className="bg-gray-800/50 rounded p-3">
                <p className="text-xs text-gray-400">Total Projected</p>
                <p className="text-lg font-bold text-gray-300">R{Math.round(totals.projected)}</p>
              </div>
              <div className="bg-gray-800/50 rounded p-3">
                <p className="text-xs text-gray-400">Total Collected</p>
                <p className="text-lg font-bold text-green-400">R{Math.round(totals.collected)}</p>
              </div>
              <div className="bg-gray-800/50 rounded p-3">
                <p className="text-xs text-gray-400">Total Shortfall</p>
                <p className="text-lg font-bold text-red-400">R{Math.round(totals.shortfall)}</p>
              </div>
              <div className="bg-gray-800/50 rounded p-3">
                <p className="text-xs text-gray-400">Total Surplus</p>
                <p className="text-lg font-bold text-green-400">R{Math.round(totals.surplus)}</p>
              </div>
              <div className="bg-gray-800/50 rounded p-3">
                <p className="text-xs text-gray-400">Total Damages</p>
                <p className="text-lg font-bold text-red-400">R{Math.round(totalDamages)}</p>
              </div>
              <div className={`rounded p-3 ${netUnaccounted > 0 ? 'bg-red-900/30 border border-red-800' : 'bg-green-900/30 border border-green-800'}`}>
                <p className="text-xs text-gray-400">NET UNACCOUNTED</p>
                <p className={`text-lg font-bold ${netUnaccounted > 0 ? 'text-red-400' : 'text-green-400'}`}>
                  R{Math.round(netUnaccounted)}
                </p>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

// --- Credit Tracker (outstanding balances per site + individual customers) ---
const CREDIT_SEED_CUSTOMERS = [
  { name: 'Taki', openingBalance: 13, date: '2026-03-01', note: 'March 2026 balance' },
  { name: 'Mashudu & Zoe', openingBalance: 609, date: '2026-03-01', note: 'March 2026 balance' },
  { name: 'Vho-Vicky', openingBalance: 120, date: '2026-03-01', note: 'March 2026 balance' },
  { name: 'Vho-Julie', openingBalance: 240, date: '2026-03-01', note: 'March 2026 balance' },
]

function CreditTrackerView() {
  const { addToast } = useToast()
  const { dept, getDistPoints } = useDeptConfig()
  const points = useMemo(() => getDistPoints(), [])
  const [customers, setCustomers] = useState([])
  const [newName, setNewName] = useState('')
  const [newBalance, setNewBalance] = useState('')
  const [newDate, setNewDate] = useState(new Date().toISOString().split('T')[0])
  const [newNote, setNewNote] = useState('')
  const [showAdd, setShowAdd] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)

  // Load credit entries (no auto-seed — customers are user-managed)
  const loadCredits = useCallback(() => {
    const entries = getCreditEntries(dept)
    setCustomers(entries.filter(e => e.entityType === 'customer'))
  }, [dept])

  useEffect(() => { loadCredits() }, [loadCredits, refreshKey])

  // Site outstanding: aggregate ALL daily collections shortfalls
  const siteOutstanding = useMemo(() => {
    const allColl = getDailyCollections(dept)
    const map = {}
    for (const c of allColl) {
      const diff = (Number(c.projected) || 0) - (Number(c.collected) || 0)
      if (!map[c.siteCode]) {
        const pt = points.find(p => p.id === c.siteCode)
        map[c.siteCode] = { siteCode: c.siteCode, siteName: pt?.name || c.siteName || c.siteCode, projected: 0, collected: 0, outstanding: 0, shortDays: 0 }
      }
      map[c.siteCode].projected += Number(c.projected) || 0
      map[c.siteCode].collected += Number(c.collected) || 0
      if (diff > 0) { map[c.siteCode].outstanding += diff; map[c.siteCode].shortDays++ }
    }
    return Object.values(map)
  }, [dept, points, refreshKey])

  const handleAddCustomer = () => {
    if (!newName.trim()) return
    const code = `cust_${newName.trim().toLowerCase().replace(/[^a-z0-9]/g, '_')}`
    saveCreditEntry(dept, code, {
      entityType: 'customer',
      entityName: newName.trim(),
      openingBalance: Number(newBalance) || 0,
      openingBalanceDate: newDate,
      openingBalanceNote: newNote,
      totalPaid: 0,
    })
    addToast(`Added customer: ${newName}`, 'success')
    setNewName(''); setNewBalance(''); setNewNote(''); setShowAdd(false)
    setRefreshKey(k => k + 1)
  }

  const handleMarkPaid = (entityCode, entityName) => {
    const entry = customers.find(c => c.entityCode === entityCode)
    if (!entry) return
    saveCreditEntry(dept, entityCode, { ...entry, totalPaid: entry.openingBalance })
    addToast(`${entityName} marked as paid`, 'success')
    setRefreshKey(k => k + 1)
  }

  const handleDelete = (entityCode) => {
    if (!confirm('Delete this customer credit entry?')) return
    deleteCreditEntry(dept, entityCode)
    setRefreshKey(k => k + 1)
  }

  const totalSiteOutstanding = siteOutstanding.reduce((s, r) => s + r.outstanding, 0)
  const totalCustomerOutstanding = customers.reduce((s, c) => s + Math.max(0, (c.openingBalance || 0) - (c.totalPaid || 0)), 0)
  const grandTotal = totalSiteOutstanding + totalCustomerOutstanding

  return (
    <div className="space-y-6 max-w-4xl">
      <h2 className="text-lg font-bold text-white">Credit Tracker</h2>

      {/* Section 1: Site outstanding */}
      <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
        <h3 className="text-sm font-medium mb-3">Sites — All-Time Outstanding</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-gray-400 text-xs border-b border-gray-800">
                <th className="text-left py-2 px-2">Site</th>
                <th className="text-right py-2 px-2">All-Time Projected</th>
                <th className="text-right py-2 px-2">All-Time Collected</th>
                <th className="text-right py-2 px-2">Outstanding</th>
                <th className="text-right py-2 px-2">Short Days</th>
                <th className="text-center py-2 px-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {siteOutstanding.length === 0 ? (
                <tr><td colSpan={6} className="py-4 text-center text-gray-500 text-sm">No collection data recorded yet</td></tr>
              ) : siteOutstanding.map(s => (
                <tr key={s.siteCode} className={`border-b border-gray-800/50 ${s.outstanding > 0 ? 'bg-red-950/10' : ''}`}>
                  <td className="py-2 px-2 text-white">{s.siteName}</td>
                  <td className="py-2 px-2 text-right text-gray-400">R{Math.round(s.projected)}</td>
                  <td className="py-2 px-2 text-right">R{Math.round(s.collected)}</td>
                  <td className="py-2 px-2 text-right text-red-400">{s.outstanding > 0 ? `R${Math.round(s.outstanding)}` : '—'}</td>
                  <td className="py-2 px-2 text-right">{s.shortDays || '—'}</td>
                  <td className="py-2 px-2 text-center text-xs">
                    {s.outstanding > 0 ? <span className="text-amber-400">R{Math.round(s.outstanding)} outstanding</span> : <span className="text-green-400">No outstanding</span>}
                  </td>
                </tr>
              ))}
            </tbody>
            {totalSiteOutstanding > 0 && (
              <tfoot>
                <tr className="border-t-2 border-gray-700 font-semibold">
                  <td colSpan={3} className="py-2 px-2 text-right">Total Site Outstanding</td>
                  <td className="py-2 px-2 text-right text-red-400">R{Math.round(totalSiteOutstanding)}</td>
                  <td colSpan={2} />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* Section 2: Individual customers */}
      <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium">Individual Customers</h3>
          <button onClick={() => setShowAdd(!showAdd)} className="flex items-center gap-1 text-xs text-gray-400 hover:text-white bg-gray-800 px-3 py-1.5 rounded">
            <Plus size={12} /> Add Customer
          </button>
        </div>

        {showAdd && (
          <div className="bg-gray-800/50 rounded p-3 mb-3 space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <input type="text" value={newName} onChange={e => setNewName(e.target.value)} placeholder="Customer name" className="bg-gray-900 border border-gray-700 rounded px-3 py-1.5 text-sm text-white" />
              <ArithmeticInput value={newBalance} onChange={(val) => setNewBalance(val == null ? '' : val)} placeholder="Opening balance (R)" className="bg-gray-900 border border-gray-700 rounded px-3 py-1.5 text-sm text-white" />
              <input type="date" value={newDate} onChange={e => setNewDate(e.target.value)} className="bg-gray-900 border border-gray-700 rounded px-3 py-1.5 text-sm text-white" />
              <input type="text" value={newNote} onChange={e => setNewNote(e.target.value)} placeholder="Note" className="bg-gray-900 border border-gray-700 rounded px-3 py-1.5 text-sm text-white" />
            </div>
            <button onClick={handleAddCustomer} className="bg-brand-600 hover:bg-brand-500 text-white px-4 py-1.5 rounded text-xs font-medium">Save Customer</button>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-gray-400 text-xs border-b border-gray-800">
                <th className="text-left py-2 px-2">Customer</th>
                <th className="text-right py-2 px-2">Opening Balance</th>
                <th className="text-left py-2 px-2">Date</th>
                <th className="text-left py-2 px-2">Note</th>
                <th className="text-right py-2 px-2">Outstanding</th>
                <th className="text-center py-2 px-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {customers.map(c => {
                const outstanding = Math.max(0, (c.openingBalance || 0) - (c.totalPaid || 0))
                return (
                  <tr key={c.entityCode} className="border-b border-gray-800/50">
                    <td className="py-2 px-2 text-white">{c.entityName}</td>
                    <td className="py-2 px-2 text-right">R{c.openingBalance || 0}</td>
                    <td className="py-2 px-2 text-xs text-gray-400">{c.openingBalanceDate || '—'}</td>
                    <td className="py-2 px-2 text-xs text-gray-400">{c.openingBalanceNote || '—'}</td>
                    <td className={`py-2 px-2 text-right font-medium ${outstanding > 0 ? 'text-red-400' : 'text-green-400'}`}>
                      {outstanding > 0 ? `R${outstanding}` : 'Paid'}
                    </td>
                    <td className="py-2 px-2 text-center">
                      <div className="flex items-center justify-center gap-2">
                        {outstanding > 0 && (
                          <button onClick={() => handleMarkPaid(c.entityCode, c.entityName)} className="text-xs text-green-400 hover:text-green-300 bg-green-900/20 px-2 py-1 rounded">
                            Mark Paid
                          </button>
                        )}
                        <button onClick={() => handleDelete(c.entityCode)} className="text-xs text-gray-500 hover:text-red-400">
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Grand total */}
      <div className={`rounded-lg p-4 ${grandTotal > 0 ? 'bg-red-900/30 border border-red-800' : 'bg-green-900/30 border border-green-800'}`}>
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <p className="text-xs text-gray-400">Sites Outstanding</p>
            <p className="text-lg font-bold text-red-400">R{Math.round(totalSiteOutstanding)}</p>
          </div>
          <div>
            <p className="text-xs text-gray-400">Customers Outstanding</p>
            <p className="text-lg font-bold text-red-400">R{Math.round(totalCustomerOutstanding)}</p>
          </div>
          <div>
            <p className="text-xs text-gray-400">GRAND TOTAL</p>
            <p className={`text-xl font-bold ${grandTotal > 0 ? 'text-red-400' : 'text-green-400'}`}>R{Math.round(grandTotal)}</p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default function BakeryEntry({ dataType, date, onSaved }) {
  // All sub-forms receive the shared date from the parent Ingestion page
  const today = new Date().toISOString().split('T')[0]
  const effectiveDate = date || today

  let inner = null
  if (dataType === 'production') inner = <ProductionForm date={effectiveDate} onSaved={onSaved} />
  else if (dataType === 'distribution') inner = <DistributionForm date={effectiveDate} onSaved={onSaved} />
  else if (dataType === 'sandwiches') inner = <DistributionForm date={effectiveDate} onSaved={onSaved} />
  else if (dataType === 'summary') inner = <BakerySummaryView date={effectiveDate} />
  else if (dataType === 'expenditure') inner = <ExpenditureForm onSaved={onSaved} />
  else if (dataType === 'cash') inner = <CashOnHandForm onSaved={onSaved} />
  else if (dataType === 'shop-sales') inner = <ShopSalesForm date={effectiveDate} onSaved={onSaved} />
  else if (dataType === 'location-cash') inner = <LocationCashForm date={effectiveDate} onSaved={onSaved} />
  else if (dataType === 'daily-collections') inner = <DailyCollectionsForm date={effectiveDate} onSaved={onSaved} />
  else if (dataType === 'damages') inner = <DamagesForm date={effectiveDate} onSaved={onSaved} />
  else if (dataType === 'monthly-summary') inner = <MonthlySummaryView date={effectiveDate} />
  else if (dataType === 'credit-tracker') inner = <CreditTrackerView />
  else return null

  return (
    <div className="space-y-3">
      <ArithmeticTip />
      {inner}
    </div>
  )
}
